/* eslint-disable camelcase */
import React, { Fragment, useState } from 'react';
import moment from 'moment-timezone'
import { StyleSheet, css } from 'aphrodite';
import { Helper } from '../common/helpers/Helper'
import { ApiHelper } from '../common/helpers/ApiHelper'
import { FilterHelper } from '@slx/slixta-common'
import { UiHelper } from '../common/helpers/UiHelper';
import { StaffHelper } from '../common/helpers/StaffHelper';
import { List, ListItem } from '@material-ui/core';
import { AppTheme } from '../styles/AppTheme';
import { Controller } from '../common/Controller';
import { AppHelper } from './AppHelper';
import { CdpHelper } from './CdpHelper';
import { AppConfig } from '../AppConfig';
import { DynamicFormDialog } from '../common/components/DynamicFormDialog';
import { MenuItemsDropdown } from '../common/components/MenuItemsDropdown';
import { WhatsappUtils } from '../screens/admin/cdp/WhatsappUtils';

const CRM_FILTER_CONTAINS_OPERATOR_CONF = {
  operators: [
    { key: 'equals', label: 'equals' },
    { key: 'not equals', label: 'not equals' },
    { key: 'contains', label: 'contains' },
  ],
  operatorVariants: [
    {
      operatorCondition: { values: ['contains'] },
      type: 'edit_text',
    },
  ],
}

/** Job title contains: multi-value chips (OR); free-form only (no preset options list) */
const CRM_FILTER_JOB_TITLE_CONTAINS_OPERATOR_CONF = {
  operators: CRM_FILTER_CONTAINS_OPERATOR_CONF.operators,
  operatorVariants: [
    {
      operatorCondition: { values: ['contains'] },
      type: 'check_box',
      multiSelect: true,
      addOptions: true,
      addOptionsLocal: true,
    },
  ],
}

export class CRMHelper {

  static getStartOfDateRange(type, today) {
    let start
    if (type === 'this_week') {
      start = today.clone().startOf('isoWeek')
    } else if (type === 'this_month') {
      start = today.clone().startOf('month')
    } else {
      throw new Error('Invalid type. Use "this_week" or "this_month".')
    }
    return start
  }

  static getDateRangeStartTs(type, timezone) {
    const tz = timezone || Helper.getLocalTimezone()
    const today = moment.tz(tz)
    const start = this.getStartOfDateRange(type,today)
    const dateTsList = []
    const day = start.clone()
    while (day.isSameOrBefore(today, 'day')) {
      dateTsList.push(day.startOf('day').valueOf())
      day.add(1, 'day')
    }

    return dateTsList
  }

  static getDateRangeLabels(type, timezone) {
    const tz = timezone || Helper.getLocalTimezone()
    const today = moment.tz(tz)
    const start = this.getStartOfDateRange(type,today)
    const labels = []
    const day = start.clone()
    while (day.isSameOrBefore(today, 'day')) {
      labels.push(day.format('MMM DD'))
      day.add(1, 'day')
    }

    return labels
  }

  static getFilterOptions(fieldDef, filterFormDefConf, currentUrl, accountFieldDef) {
    let filterQueryData = ''
    let filterFormDef = [], filterFormData = {}
    currentUrl = currentUrl || document.location.href
    const showInternal = Helper.isShowInternal()
    if(fieldDef && filterFormDefConf) {
      const isMobileView = Helper.isMobileView()
      filterFormDef = FilterHelper.getFilterFormDefForCRM(fieldDef, filterFormDefConf, accountFieldDef)
      filterFormDef.forEach((field)=>{
        if(isMobileView && ['radio','check_box'].includes(field.type)) {
          field.disableTyping = true
        }
      })
      try {
        const filterUrl = new URL(currentUrl)
        if(filterUrl.searchParams.has('date')) {
          const createdTs = filterUrl.searchParams.get('date')
          filterUrl.searchParams.delete('date')
          filterUrl.searchParams.set('createdTs',createdTs)
          currentUrl = filterUrl.toString()
        }
      } catch {
        console.log(`Invalid URL: "${currentUrl}"`)
      }
      filterQueryData = FilterHelper.getFilterQueryData(currentUrl)
      filterFormData = FilterHelper.getFilterFormData(filterFormDef, currentUrl, Helper.getLocalTimezone())

      if(filterFormData && filterFormDef) {
        Object.keys(filterFormData).forEach((key)=>{
          if (filterFormDef.some(fo => fo.key === key)) {
            return
          }
          const normalizedK = FilterHelper.getFilterBaseKey(key)

          const field = filterFormDef.find(fo => fo.key === normalizedK)
          let hasAny = false
          let hasNone = false
          if(field && field.operators) {
            hasAny = field.operators.some(op => op.key === 'any')
            hasNone = field.operators.some(op => op.key === 'none')
          }

          if(field) {
            if(key.endsWith('~')) {
              field.selectedOperator = 'all'
            } else if(key.endsWith('!')) {
              field.selectedOperator = hasNone ? 'none' : 'not equals'
            } else if(key.endsWith('*')) {
              field.selectedOperator = 'contains'
            } else {
              field.selectedOperator = hasAny ? 'any' : 'equals'
            }
          }
        })
      }
    }
    return { filterOptions: filterFormDef, searchText: filterQueryData, searchFilters: filterFormData, showInternal }
  }

  static updateWindowUrl(searchText = '', searchFilters = {}, timezone = Helper.getLocalTimezone()) {
    const _url = new URL(document.location.href)
    const urlSearchParamsKeys = [..._url.searchParams.keys()]
    for (const urlSearchParamsKey of urlSearchParamsKeys) {
      _url.searchParams.delete(urlSearchParamsKey)
    }

    const newQueryString = FilterHelper.processFilterFormSubmission({filterFormData: searchFilters, filterQueryData: searchText, timezone})
    const newSearchParams = new URLSearchParams(newQueryString);
    for (const [key, value] of newSearchParams.entries()) {
      _url.searchParams.set(key, value);
    }

    window.history.replaceState(null, '', _url.toString());
  }

  static normalizeAssigneeValue(value) {
    if (value === undefined || value === null || value === '') {
      return value
    }
    if (Array.isArray(value)) {
      return value.map(v => CRMHelper.normalizeAssigneeValue(v))
    }
    if (typeof value !== 'string') {
      return value
    }
    if (['unassigned', '__UNASSIGNED__', 'none', 'current-user'].includes(value)) {
      return value
    }
    return CRMHelper.getUserIdFromEnityId(value)
  }

  static normalizeAssigneeFieldValues(fieldDef, keys = ['assignee']) {
    if (!fieldDef || !Array.isArray(keys) || keys.length === 0) {
      return
    }
    keys.forEach((key) => {
      const fields = Helper.getFields(fieldDef, key)
      fields.forEach((field) => {
        if (!field) {
          return
        }
        if (field.value !== undefined && field.value !== null) {
          field.value = CRMHelper.normalizeAssigneeValue(field.value)
        }
        if (field.defaultValue !== undefined && field.defaultValue !== null) {
          field.defaultValue = CRMHelper.normalizeAssigneeValue(field.defaultValue)
        }
      })
    })
  }

  static normalizeAssigneeFilterValue(value) {
    if (!value || typeof value !== 'string') {
      return value
    }
    return value.split(',').map((token) => {
      if (token === 'none') {
        return ''
      }
      return CRMHelper.normalizeAssigneeValue(token)
    }).join(',')
  }

  static constructSearchFiltersQuery(searchFilters = {}, filterOptions = [], showInternal = false, assignee) {
    const searchFiltersQuery = FilterHelper.getApiQueryFromFilterFormData(searchFilters, filterOptions, Helper.getLocalTimezone())

    if (!showInternal) {
      const tagsArray = (searchFiltersQuery['tags!'] || '').split(',').filter(t=>!!t)
      if (!tagsArray.includes('INTERNAL')) { tagsArray.push('INTERNAL') }
      searchFiltersQuery['tags!'] = tagsArray.join(',');
    }

    ['assignee', 'assignee!'].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(searchFiltersQuery, key)) {
        searchFiltersQuery[key] = CRMHelper.normalizeAssigneeFilterValue(searchFiltersQuery[key])
      }
    })

    if (assignee) {
      searchFiltersQuery.assignee = assignee
    }

    return searchFiltersQuery
  }

  static getDateRangeCustomFieldKeys(customFields) {
    const dateRangeCustomFieldKeys = []
    if(Array.isArray(customFields) && customFields.length > 0) {
      customFields.filter((field) => field.type === 'dateRange').forEach((field) => dateRangeCustomFieldKeys.push(field.key))
    }
    return dateRangeCustomFieldKeys
  }

  static getAdvancedCustomFieldKeys(customFields) {
    const advancedCustomFieldKeys = []
    if(Array.isArray(customFields) && customFields.length > 0) {
      customFields.filter((field) => field.advanced).forEach((field) => advancedCustomFieldKeys.push(field.key))
    }
    return advancedCustomFieldKeys
  }

  static getCrmFieldOptions(fieldDef, extraFields = []) {
    const fields = fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields
    const allFields = [
      ...(Array.isArray(fields) ? fields : []),
      ...(Array.isArray(extraFields) ? extraFields : []),
    ]
    if (allFields.length === 0) return []

    const seenKeys = new Set()
    const options = []

    allFields.forEach((field) => {
      if (!field) return
      const key = (field.key && String(field.key).startsWith('account.'))
        ? field.key
        : (field._uiKey || field.key)
      if (!key || seenKeys.has(key)) return
      seenKeys.add(key)
      options.push({
        key,
        label: field.label || field.title || key,
      })
    })

    return options.sort((a, b) => a.label.localeCompare(b.label))
  }

  /** Composite display keys for CRM_CONTACT_PREVIEW_TEMPLATE (not real contact fields). */
  static getCrmContactPreviewCompositeFieldOptions() {
    return [
      { key: 'name', label: 'Name' },
      { key: 'jobDescription', label: 'Job Description' },
    ]
  }

  /** Bare stats/chart keys on Accounts dashboard (also used as account.* on Contacts ABM). */
  static ACCOUNT_DASHBOARD_CHART_BASE_KEYS = ['industry', 'city', 'country', 'employeeCount', 'tags']

  /** Chart tab click: prefer bucket key over display label for drill-down. */
  static isAccountDashboardChartTabKey(tabKey) {
    if (tabKey == null || tabKey === '') return false
    const k = String(tabKey)
    if (k.startsWith('account.')) return true
    return CRMHelper.ACCOUNT_DASHBOARD_CHART_BASE_KEYS.includes(k)
  }

  /** Chart filter apply (contact tags use a separate branch when key is bare `tags`). */
  static isAccountDashboardChartFilterKey(chartKey) {
    if (chartKey == null || chartKey === '') return false
    const k = String(chartKey)
    if (k.startsWith('account.')) return true
    return ['industry', 'city', 'country', 'employeeCount'].includes(k)
  }

  static getCrmContactJobTitleFilterOptions(fieldDef, crmContactsStats) {
    const jobTitleField = Helper.getField(fieldDef, 'jobTitle')
    if (jobTitleField && Array.isArray(jobTitleField.options) && jobTitleField.options.length > 0) {
      return jobTitleField.options
    }
    if (crmContactsStats && crmContactsStats.jobTitle && Array.isArray(crmContactsStats.jobTitle.buckets)) {
      return crmContactsStats.jobTitle.buckets
        .map((bucket) => ({ key: bucket.key, label: bucket.key }))
        .filter((o) => !!o.key)
    }
    return null
  }

  static buildCrmContactFilterFormDefConf({
    fieldDef,
    accountFieldDef,
    crmSequences,
    crmInboxes,
    crmBulkImportJobs,
    crmContactsStats,
    orgId,
    currentUser,
    subOrgs,
    staffUsers,
    isLeadRoutingEnabled,
    currentUrl = (typeof document !== 'undefined' ? document.location.href : ''),
  } = {}) {
    const showInternal = Helper.isShowInternal()
    const customFields = CdpHelper.generateCustomFields(fieldDef, 'left')
    const accountFilterFields = (AppHelper.isABMEnabled(orgId) && accountFieldDef)
      ? CdpHelper.generateCustomFields(accountFieldDef, 'left')
      : []
    const advancedCustomFieldKeys = CRMHelper.getAdvancedCustomFieldKeys(customFields)
    const accountFilterKeys = accountFilterFields.map((f) => f.key)
    const dateRangeFieldsConf = {}
    if (Array.isArray(customFields) && customFields.length > 0) {
      customFields.filter((field) => field.type === 'dateRange').forEach((field) => {
        dateRangeFieldsConf[field.key] = {
          timezone: Helper.getLocalTimezone(),
          availableDateRanges: field.availableDateRanges,
        }
      })
    }
    if (Array.isArray(accountFilterFields) && accountFilterFields.length > 0) {
      accountFilterFields.filter((field) => field.type === 'dateRange').forEach((field) => {
        dateRangeFieldsConf[field.key] = {
          timezone: Helper.getLocalTimezone(),
          availableDateRanges: field.availableDateRanges,
        }
      })
    }
    let _url
    try {
      _url = new URL(currentUrl)
    } catch {
      _url = null
    }
    const createdModifiedDateRanges = ['today', 'yesterday', 'this_week', 'this_month', 'last_7d', 'last_30d', 'last_90d', 'last_6m', 'last_12m', 'current_7d', 'current_30d', 'custom', 'all_time']

    const accountFilterFieldsConf = {}
    accountFilterFields.forEach((field) => {
      if (!field || !field.key) return
      const conf = {}
      if (Array.isArray(field.options) && field.options.length > 0) {
        conf.options = field.options
      }
      if (Object.keys(conf).length > 0) {
        accountFilterFieldsConf[field.key] = conf
      }
    })

    const jobTitleOptions = CRMHelper.getCrmContactJobTitleFilterOptions(fieldDef, crmContactsStats)

    return {
      fields: {
        tags: (() => {
          const fieldData = {}
          const tagsField = fieldDef ? JSON.parse(JSON.stringify(Helper.getField(fieldDef, 'tags'))) : null
          if (tagsField) {
            fieldData.operators = [{ key: 'any', label: 'any' }, { key: 'all', label: 'all' }]
          }
          if (tagsField && tagsField.options && !showInternal) {
            fieldData.options = tagsField.options.filter((option) => option.key !== 'INTERNAL')
          }
          return fieldData
        })(),
        'tags!': (() => {
          const fieldData = {}
          const tagsField = fieldDef ? JSON.parse(JSON.stringify(Helper.getField(fieldDef, 'tags'))) : null
          if (tagsField) {
            fieldData.operators = null
          }
          if (tagsField && tagsField.options && !showInternal) {
            fieldData.options = tagsField.options.filter((option) => option.key !== 'INTERNAL')
          }
          return fieldData
        })(),
        subscriptions: (() => {
          const fieldData = {}
          const subscriptionsField = fieldDef
            ? JSON.parse(JSON.stringify(Helper.getField(fieldDef, 'subscriptions')))
            : null
          if (subscriptionsField && subscriptionsField.options) {
            subscriptionsField.options.unshift({ key: 'none', text: 'NONE' })
            fieldData.options = subscriptionsField.options
          }
          return fieldData
        })(),
        campaigns: (() => {
          const fieldData = {}
          const sequencesFieldDef = UiHelper.getSequencesFieldDef(crmSequences, crmInboxes)
          const sequencesField = sequencesFieldDef
            ? JSON.parse(JSON.stringify(Helper.getField(sequencesFieldDef, 'campaignId')))
            : null
          if (sequencesField && sequencesField.options) {
            sequencesField.options.unshift({ key: 'any', text: 'ANY' })
            fieldData.options = sequencesField.options
          }
          return fieldData
        })(),
        importJobId: {
          options: crmBulkImportJobs && crmBulkImportJobs.map((j) => ({
            key: j.id,
            label: j.title || Helper.formatTimestamp(j.createdTs),
          })),
        },
        createdTs: {
          timezone: (_url && _url.searchParams && _url.searchParams.has('_datetimezone'))
            ? _url.searchParams.get('_datetimezone')
            : Helper.getLocalTimezone(),
          disabled: (_url && _url.searchParams && _url.searchParams.has('_datetimezone')) ? true : false,
          availableDateRanges: createdModifiedDateRanges,
        },
        modifiedTs: {
          timezone: (_url && _url.searchParams && _url.searchParams.has('_datetimezone'))
            ? _url.searchParams.get('_datetimezone')
            : Helper.getLocalTimezone(),
          disabled: (_url && _url.searchParams && _url.searchParams.has('_datetimezone')) ? true : false,
          availableDateRanges: createdModifiedDateRanges,
        },
        assignee: (() => {
          const fieldData = {}
          const assigneeField = fieldDef
            ? JSON.parse(JSON.stringify(Helper.getField(fieldDef, 'assignee')))
            : null
          if (assigneeField && assigneeField.options) {
            assigneeField.options.unshift({ key: 'none', text: '[UNASSIGNED]' })
            assigneeField.options.unshift({ key: 'current-user', text: '[CURRENT USER]' })
            fieldData.options = assigneeField.options
          }
          fieldData.optionsValueMapping = currentUser && { 'current-user': currentUser }
          return fieldData
        })(),
        subOrgId: (() => {
          const fieldData = {}
          const teamField = fieldDef
            ? JSON.parse(JSON.stringify(Helper.getField(fieldDef, 'subOrgId') || null))
            : null
          if (teamField) {
            if (teamField.options && !AppHelper.isTeamsUnifiedView(orgId)) {
              teamField.options.unshift({ key: 'none', text: '[UNASSIGNED]' })
            }
            fieldData.options = teamField.options
          }
          return fieldData
        })(),
        utmSource: {
          options: (crmContactsStats && crmContactsStats.utm_sources && crmContactsStats.utm_sources.buckets && crmContactsStats.utm_sources.buckets.length > 0)
            && crmContactsStats.utm_sources.buckets.map((bucket) => bucket.key).filter((a) => !!a).map((item) => ({ key: item, label: item })),
        },
        utmMedium: {
          options: (crmContactsStats && crmContactsStats.utm_mediums && crmContactsStats.utm_mediums.buckets && crmContactsStats.utm_mediums.buckets.length > 0)
            && crmContactsStats.utm_mediums.buckets.map((bucket) => bucket.key).filter((a) => !!a).map((item) => ({ key: item, label: item })),
        },
        utmCampaign: {
          options: (crmContactsStats && crmContactsStats.utm_campaigns && crmContactsStats.utm_campaigns.buckets && crmContactsStats.utm_campaigns.buckets.length > 0)
            && crmContactsStats.utm_campaigns.buckets.map((bucket) => bucket.key).filter((a) => !!a).map((item) => ({ key: item, label: item })),
          ...CRM_FILTER_CONTAINS_OPERATOR_CONF,
        },
        utmContent: {
          options: (crmContactsStats && crmContactsStats.utm_contents && crmContactsStats.utm_contents.buckets && crmContactsStats.utm_contents.buckets.length > 0)
            && crmContactsStats.utm_contents.buckets.map((bucket) => bucket.key).filter((a) => !!a).map((item) => ({ key: item, label: item })),
          ...CRM_FILTER_CONTAINS_OPERATOR_CONF,
        },
        utmTerm: {
          options: (crmContactsStats && crmContactsStats.utm_terms && crmContactsStats.utm_terms.buckets && crmContactsStats.utm_terms.buckets.length > 0)
            && crmContactsStats.utm_terms.buckets.map((bucket) => bucket.key).filter((a) => !!a).map((item) => ({ key: item, label: item })),
          ...CRM_FILTER_CONTAINS_OPERATOR_CONF,
        },
        utmId: {
          options: (crmContactsStats && crmContactsStats.utm_ids && crmContactsStats.utm_ids.buckets && crmContactsStats.utm_ids.buckets.length > 0)
            && crmContactsStats.utm_ids.buckets.map((bucket) => bucket.key).filter((a) => !!a).map((item) => ({ key: item, label: item })),
          disabled: (_url && _url.searchParams && _url.searchParams.has('_labelForUtmId')) ? true : false,
          addOptions: true,
          addOptionsLocal: true,
        },
        slxAdId: {
          options: (crmContactsStats && crmContactsStats.slx_ad_ids && crmContactsStats.slx_ad_ids.buckets && crmContactsStats.slx_ad_ids.buckets.length > 0)
            && crmContactsStats.slx_ad_ids.buckets.map((bucket) => bucket.key).filter((a) => !!a).map((item) => ({ key: item, label: item })),
          disabled: (_url && _url.searchParams && _url.searchParams.has('_labelForSlxAdId')) ? true : false,
          addOptions: true,
          addOptionsLocal: true,
        },
        lastIntSentTs: {
          timezone: Helper.getLocalTimezone(),
        },
        lastWABroadcastTs: {
          timezone: Helper.getLocalTimezone(),
        },
        lastEmailBroadcastTs: {
          timezone: Helper.getLocalTimezone(),
        },
        lastDripSequenceTs: {
          timezone: Helper.getLocalTimezone(),
        },
        lastOutreachTs: {
          timezone: Helper.getLocalTimezone(),
        },
        routedAssignee: {
          options: [
            { key: 'none', text: '[UNASSIGNED]' },
            { key: 'current-user', text: '[CURRENT USER]' },
            ...(staffUsers ? UiHelper.retrieveOptions(staffUsers) : []),
          ],
        },
        routedSubOrgId: {
          options: [
            { key: 'none', text: '[UNASSIGNED]' },
            ...(subOrgs ? UiHelper.retrieveOptions(subOrgs) : []),
          ],
        },
        _hasattrib: {
          options: CRMHelper.getCrmFieldOptions(fieldDef, accountFilterFields),
        },
        '_hasattrib!': {
          options: CRMHelper.getCrmFieldOptions(fieldDef, accountFilterFields),
        },
        ...(AppHelper.isABMEnabled(orgId) && jobTitleOptions
          ? { jobTitle: { options: jobTitleOptions, ...CRM_FILTER_JOB_TITLE_CONTAINS_OPERATOR_CONF } }
          : {}),
        ...accountFilterFieldsConf,
        ...dateRangeFieldsConf,
      },
      includedFilters: (() => {
        const includedKeys = ['state', 'source', 'tags', 'tags!', 'accounts', 'primaryAccountId']
        includedKeys.push(...customFields.map((f) => f.key))
        if (AppHelper.isTeamsEnabled(orgId) && !AppHelper.isRestrictViewToAssignedContacts(orgId)) {
          includedKeys.push('subOrgId')
        }
        if (!AppHelper.isRestrictViewToAssignedContacts(orgId)) {
          includedKeys.push('assignee')
        }
        includedKeys.push('createdBy', 'createdTs', 'modifiedTs')
        if (AppHelper.isABMEnabled(orgId)) {
          if (jobTitleOptions) {
            includedKeys.push('jobTitle')
          }
          includedKeys.push(...accountFilterFields.map((f) => f.key))
        }
        includedKeys.push(
          'campaigns', 'subscriptions', 'importJobId', 'emailProvider',
          'linkedinConnectionStatus', 'utmSource', 'utmMedium', 'utmCampaign', 'utmContent', 'utmTerm', 'utmId', 'slxAdId',
          'lastIntSentTs', 'lastActiveIntSentStatus', 'lastWABroadcastTs', 'lastEmailBroadcastTs', 'lastDripSequenceTs',
          'lastOutreachTs', '_hasattrib', '_hasattrib!',
        )
        if (isLeadRoutingEnabled) {
          includedKeys.push('routedAssignee', 'routedSubOrgId')
        }
        return includedKeys
      })(),
      accountFilters: AppHelper.isABMEnabled(orgId) ? accountFilterKeys : [],
      advancedFilters: [
        'campaigns', 'emailProvider', 'importJobId', 'subscriptions', 'linkedinConnectionStatus',
        'utmSource', 'utmMedium', 'utmCampaign', 'utmContent', 'utmTerm', 'utmId', 'slxAdId',
        'lastIntSentTs', 'lastActiveIntSentStatus', 'lastWABroadcastTs', 'lastEmailBroadcastTs',
        'lastDripSequenceTs', 'lastOutreachTs', 'routedAssignee', 'routedSubOrgId', ...advancedCustomFieldKeys, '_hasattrib', '_hasattrib!',
      ],
    }
  }

  static getCrmContactStateOptions(fieldDef, orgId) {
    const stateField = Helper.getField(fieldDef, 'state')
    if (!stateField || !Array.isArray(stateField.options)) {
      return []
    }
    return stateField.options
      .filter((option) => option && option.key)
      .map((option) => ({
        key: option.key,
        label: UiHelper.renameCRMContactLeadStatus(orgId, option.label || option.key),
      }))
  }

  static populateAssigneeFieldOptions(fieldDef, subOrgs, orgId, subOrgId, clearDefaultValue = false, staffUsers = []) {
    function getAssigneeFieldOptions(options, value, orgId, subOrgId, selectedSubOrgId, staffUsers) {
      let assigneeOptions = []
      if(options && options.length > 0) {
        const currentUserId = CRMHelper.normalizeAssigneeValue(value)
        const staffSubOrgUserIds = StaffHelper.userIdsWithOrgAccess(staffUsers, subOrgId)
        const selectedSubOrgUserIds = StaffHelper.userIdsWithOrgAccess(staffUsers, selectedSubOrgId)
        const mainOrgUserIds = StaffHelper.userIdsWithOrgAccess(staffUsers, orgId)
        assigneeOptions = options.filter((assignee)=> {
          if(assignee && assignee.key) {
            if(assignee.key === value || assignee.key === currentUserId) {
              return true
            }
            if((subOrgId && staffSubOrgUserIds.has(assignee.key)) || (selectedSubOrgId && selectedSubOrgUserIds.has(assignee.key))) {
              return true
            } else if(!subOrgId && !selectedSubOrgId && mainOrgUserIds.has(assignee.key)) {
              return true
            }
          }
        })
      }
      return assigneeOptions
    }

    if(fieldDef) {
      let subOrgIds = []
      if(subOrgs && subOrgs.length > 0) {
        subOrgIds = subOrgs.map(subOrg => subOrg.id)
      }
      const assignedTeamField = Helper.getField(fieldDef, 'subOrgId')
      const assigneeField = Helper.getField(fieldDef, 'assignee')
      if(!assigneeField) {
        return
      }
      if(!subOrgId && assignedTeamField) {
        assigneeField.condition = { key: 'subOrgId', value: '', values: [''] }
        if(clearDefaultValue) {
          assigneeField.condition.clearDefaultValue = true
        }
      }
      let assigneeOptions = []
      if(assigneeField.options) {
        assigneeOptions = JSON.parse(JSON.stringify(assigneeField.options))
        assigneeField.options = getAssigneeFieldOptions(assigneeOptions,assigneeField.value,orgId,subOrgId,'',staffUsers)
        const assigneeFields = []
        if(assignedTeamField) {
          subOrgIds.forEach((id)=>{
            const assigneeFieldCp = JSON.parse(JSON.stringify(assigneeField))
            assigneeFieldCp.condition = { key: 'subOrgId', value: id, values: [id] }
            assigneeFieldCp.options = getAssigneeFieldOptions(assigneeOptions,assigneeFieldCp.value,orgId,subOrgId,id,staffUsers)
            assigneeFields.push(assigneeFieldCp)
          })
        }
        const assigneeFieldIndex = fieldDef.steps[0].fields.findIndex(f => f.key === 'assignee')
        fieldDef.steps[0].fields.splice(assigneeFieldIndex, 0, ...assigneeFields)
      }
    }
  }

  static filterAssigneeOptionsByOrgAccess(options, staffUsers, orgUnitId) {
    if (!Array.isArray(options) || !orgUnitId) {
      return options
    }
    const allowedUserIds = StaffHelper.userIdsWithOrgAccess(staffUsers, orgUnitId)
    const SENTINELS = new Set(['none', 'current-user', 'unassigned', '__UNASSIGNED__'])
    return options.filter((option) => {
      if (!option || !option.key) {
        return false
      }
      return SENTINELS.has(option.key) || allowedUserIds.has(option.key)
    })
  }

  static findStaffByAssignee(staffUsers, assignee) {
    if (!assignee || !Array.isArray(staffUsers) || staffUsers.length === 0) {
      return null
    }
    const userId = CRMHelper.getUserIdFromEnityId(assignee)
    return staffUsers.find(s => s && (s.userId === userId || s.id === assignee || s.id === userId)) || null
  }

  static normalizeAutomationAssigneeCurrent(current) {
    if (!current || typeof current !== 'object') {
      return current
    }
    const next = { ...current }
    if (next.payloadSubField === 'assignee' && next.value !== undefined && next.value !== null) {
      next.value = CRMHelper.normalizeAssigneeValue(next.value)
    }
    if (next.attrName === 'assignee') {
      if (next.attrValue !== undefined && next.attrValue !== null) {
        next.attrValue = CRMHelper.normalizeAssigneeValue(next.attrValue)
        next.attrValue__assignee = next.attrValue
      }
      if (next.values !== undefined && next.values !== null) {
        next.values = CRMHelper.normalizeAssigneeValue(next.values)
        next.values__assignee = next.values
      }
    }
    return next
  }

  static setUniqueAssigneeOptionsOnFieldDef(fieldDef, staffUsers) {
    if (!fieldDef || !fieldDef.steps || !fieldDef.steps[0] || !Array.isArray(fieldDef.steps[0].fields)) {
      return
    }
    const uniqueStaff = StaffHelper.uniqueStaffByUserId(staffUsers)
    const staffOptions = uniqueStaff.map(u => ({
      key: u.userId,
      text: u.title || Helper.formatName(u),
      label: u.title || Helper.formatName(u),
    }))
    // __RULE_UPDATE_CONTACT_CLEAR__ is the BE [CLEAR] option on UPDATE_CONTACT attrValue__assignee
    const SENTINELS = ['__RULE_UPDATE_CONTACT_CLEAR__', '__UNASSIGNED__', 'unassigned', 'none', 'current-user']
    const targetsAssignee = (v) => v === 'assignee' || (Array.isArray(v) && v.includes('assignee'))
    const conditionTargetsAssignee = (condition) => {
      if (!condition || typeof condition !== 'object') {
        return false
      }
      if (['payloadSubField', 'attrName'].includes(condition.key) &&
          (targetsAssignee(condition.value) || targetsAssignee(condition.values))) {
        return true
      }
      if (Array.isArray(condition.and) && condition.and.some(conditionTargetsAssignee)) {
        return true
      }
      if (Array.isArray(condition.or) && condition.or.some(conditionTargetsAssignee)) {
        return true
      }
      return false
    }
    const isAssigneeValueField = (field) => {
      if (!field) {
        return false
      }
      if (['attrValue__assignee', 'values__assignee'].includes(field.key)) {
        return true
      }
      if (field.key !== 'value') {
        return false
      }
      return conditionTargetsAssignee(field.condition)
    }
    fieldDef.steps[0].fields.forEach((field) => {
      if (!isAssigneeValueField(field)) {
        return
      }
      const existingOptions = Array.isArray(field.options) ? field.options : []
      const specials = existingOptions.filter(o => o && SENTINELS.includes(o.key))
      field.options = [...specials, ...staffOptions]
    })
  }

  static splitCardsIntoBalancedRows(items = [], maxCount = 7) {
    const total = items.length

    if (total === 0) return []

    const rows = Math.ceil(total / maxCount)
    const baseSize = Math.floor(total / rows)
    const remainder = total % rows

    const result = []
    let index = 0

    for (let i = 0; i < rows; i++) {
      const rowSize = baseSize + (i < remainder ? 1 : 0)
      result.push(items.slice(index, index + rowSize))
      index += rowSize
    }

    return result
  }

  static isValidURL(string) {
    try {
      new URL(string)
      return true
    } catch (_) {
      return false
    }
  }

  static getLastMonthsStartTs(currentTs, countOfMonths, timezone) {
    const result = []
    const tz = timezone || Helper.getLocalTimezone()
    let date = moment.tz(currentTs, tz).startOf('month');

    for (let i = 0; i < countOfMonths; i++) {
      result.unshift(date.valueOf())
      date = date.subtract(1, 'month')
    }

    return result
  }

  static getLastDaysStartTs(currentTs, countOfDays, timezone) {
    const result = []
    const tz = timezone || Helper.getLocalTimezone()
    let day = moment.tz(currentTs, tz).startOf('day');

    for (let i = 0; i < countOfDays; i++) {
      result.unshift(day.valueOf())
      day = day.subtract(1, 'day')
    }

    return result
  }

  static getToday24HoursTs(currentTs, timezone) {
    const result = []
    const tz = timezone || Helper.getLocalTimezone()
    let hour = moment.tz(currentTs, tz).startOf('day')

    for (let i = 0; i < 24; i++) {
      result.push(hour.valueOf())
      hour = hour.add(1, 'hour')
    }

    return result
  }

  static getYesterday24HoursTs(currentTs, timezone) {
    const result = []
    const tz = timezone || Helper.getLocalTimezone()
    let hour = moment.tz(currentTs, tz).subtract(1, 'day').startOf('day')

    for (let i = 0; i < 24; i++) {
      result.push(hour.valueOf())
      hour = hour.add(1, 'hour')
    }

    return result
  }

  static generateColor(fraction) {
    return `rgba(66, 117, 245, ${fraction.toFixed(2)})`
  }

  static getPieChartColor(key, index, numKeys) {
    switch(key) {
    case 'UNQUALIFIED': return 'darkgray'
    case 'LOST': return 'silver'
    case 'BAD TIMING': return 'lightgray'
    }
    return CRMHelper.generateColor(Number(index + 1) / numKeys)
  }

  static mergeBucketsByMaxDocCount(buckets1, buckets2) {
    const bucketsMap = new Map()
    if (buckets1 && Array.isArray(buckets1)) {
      buckets1.forEach(bucket => {
        if (bucket && bucket.key !== undefined) {
          bucketsMap.set(bucket.key, bucket.doc_count || 0)
        }
      })
    }
    if (buckets2 && Array.isArray(buckets2)) {
      buckets2.forEach(bucket => {
        if (bucket && bucket.key !== undefined) {
          const existingCount = bucketsMap.get(bucket.key) || 0
          const newCount = bucket.doc_count || 0
          bucketsMap.set(bucket.key, Math.max(existingCount, newCount))
        }
      })
    }
    const mergedBuckets = Array.from(bucketsMap.entries())
      .map(([key, doc_count]) => ({ key, doc_count }))
      .sort((a, b) => a.key - b.key)
    return mergedBuckets
  }

  static generateMultiChartData({stats, key, label, type, timezone, date, orgId}) {
    let chartData = {}
    if(type === 'pieChart') {
      chartData = CRMHelper.statsToPieData(stats,key,label,orgId)
    } else if(type === 'lineChart') {
      chartData = CRMHelper.statsToLineData(stats,key,label, date, timezone)
    } else if(type === 'barChart') {
      const barChartStats = JSON.parse(JSON.stringify(stats))
      if(key === 'tags' && barChartStats && barChartStats.tags && barChartStats.tags.buckets ) {
        const tagsExcludedFromBarChart = CdpHelper.CRM_CONTACT_CHART_EXCLUDED_TAGS || []
        const filteredBuckets = barChartStats.tags.buckets.filter((bucket)=> !(tagsExcludedFromBarChart.includes(bucket.key)))
        barChartStats.tags.buckets = filteredBuckets
      }
      chartData = CRMHelper.statsToBarData(barChartStats,key,label)
    }
    return chartData
  }

  static getChartExpansionGroups(orgId, settingKey) {
    const raw = AppHelper.getSetting(orgId, settingKey)
    if (!raw || !String(raw).trim()) {
      return null
    }
    const groups = []
    String(raw).split('|').forEach((segment, index) => {
      const obj = {}
      String(segment).split(';').forEach((part) => {
        const trimmed = part.trim()
        if (!trimmed) {
          return
        }
        const colonIdx = trimmed.indexOf(':')
        if (colonIdx === -1) {
          return
        }
        const key = trimmed.slice(0, colonIdx).trim()
        const value = trimmed.slice(colonIdx + 1).trim()
        if (key) {
          obj[key] = value
        }
      })
      const title = (obj.title || '').trim()
      const tags = String(obj.tags || '')
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
      if (!title || tags.length === 0) {
        return
      }
      groups.push({
        key: `g${index}`,
        label: title,
        keys: tags,
      })
    })
    return groups.length > 0 ? groups : null
  }

  static DEFAULT_ACCOUNT_DASHBOARD_CHARTS = 'industry:pie|employeeCount:bar|tags:bar|country:pie|city:bar'

  static ACCOUNT_CHART_TITLES = {
    industry: 'Industry',
    city: 'City',
    country: 'Country',
    employeeCount: 'Employees',
    tags: 'Account Tags',
  }

  /** Parse CRM_ACCOUNT_DASHBOARD_CHARTS: "industry:pie|city:bar|..." */
  static parseAccountDashboardCharts(settingValue) {
    const raw = (settingValue && String(settingValue).trim())
      || CRMHelper.DEFAULT_ACCOUNT_DASHBOARD_CHARTS
    return String(raw).split('|').map((part) => {
      const trimmed = part.trim()
      if (!trimmed) return null
      const [field, chartType] = trimmed.split(':').map(s => (s || '').trim())
      if (!field) return null
      const type = (chartType === 'pie' || chartType === 'pieChart') ? 'pieChart'
        : (chartType === 'bar' || chartType === 'barChart') ? 'barChart'
          : 'barChart'
      return { field, type }
    }).filter(Boolean)
  }

  /**
   * Build TabChartCard tab entries from CRM_ACCOUNT_DASHBOARD_CHARTS.
   * keyPrefix: '' on Accounts page, 'account.' on Contacts (ABM).
   * Hides tabs with no buckets when stats provided.
   */
  static buildAccountDashboardChartTabs({
    orgId,
    stats,
    keyPrefix = '',
    label = 'Count',
    tableRowActions,
    filterClickHandler,
  } = {}) {
    const settingValue = orgId ? AppHelper.getSetting(orgId, 'CRM_ACCOUNT_DASHBOARD_CHARTS') : ''
    const parsed = CRMHelper.parseAccountDashboardCharts(settingValue)
    return parsed.map(({ field, type }) => {
      const statsKey = `${keyPrefix}${field}`
      const statsAgg = stats && stats[statsKey]
      const buckets = statsAgg && Array.isArray(statsAgg.buckets)
        ? statsAgg.buckets.filter(b => b && b.key != null && b.key !== '')
        : null
      // Hide only when we know the agg exists and has no non-empty buckets
      if (buckets && buckets.length === 0) {
        return null
      }
      const title = CRMHelper.ACCOUNT_CHART_TITLES[field]
        || field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
      return {
        chart: statsKey,
        title,
        key: statsKey,
        label,
        type,
        enableExpansion: true,
        headers: [title, 'Count'],
        searchColumns: [title],
        searchPlaceholder: `Search by ${title}`,
        showPercent: true,
        enableFilter: true,
        tableRowActions,
        filterClickHandler,
      }
    }).filter(Boolean)
  }

  static CRM_ACCOUNT_CATALOG_OPTION_KEYS = ['industry', 'city', 'country']

  /** Resolve a step field by storage key or `_uiKey` (e.g. industry → customselectsingle1). */
  static findFormField(fieldDef, fieldKey) {
    if (!fieldDef || !fieldDef.steps || !fieldDef.steps[0] || !Array.isArray(fieldDef.steps[0].fields) || !fieldKey) {
      return null
    }
    return fieldDef.steps[0].fields.find(f => f && (f.key === fieldKey || f._uiKey === fieldKey)) || null
  }

  /** Stats for account form select enrichment (list aggregations, not contact stats). */
  static getCrmAccountFormEnrichmentStats(crmAccountsResult) {
    return (crmAccountsResult && crmAccountsResult.stats) || null
  }

  /** Merge option entries into a field without dropping existing options. */
  static mergeOptionListIntoField(fieldDef, optionList, fieldKey) {
    if (!fieldDef || !fieldDef.steps || !fieldDef.steps[0] || !Array.isArray(fieldDef.steps[0].fields)) return
    if (!Array.isArray(optionList) || optionList.length === 0) return
    const field = CRMHelper.findFormField(fieldDef, fieldKey)
    if (!field) return
    const existing = Array.isArray(field.options) ? field.options : []
    const merged = [...existing]
    optionList.forEach((opt) => {
      if (!opt || opt.key == null || opt.key === '') return
      if (!merged.some(o => o && String(o.key) === String(opt.key))) {
        merged.push({ key: String(opt.key), label: String(opt.label != null ? opt.label : opt.key) })
      }
    })
    field.options = merged
  }

  /** Merge catalog attr options into an existing fieldDef field without dropping stats options. */
  static mergeAttrOptionsIntoField(fieldDef, attrs, attrKey, fieldKey) {
    if (!fieldDef || !attrs) return
    const catalog = Helper.extractAttrs(attrs, attrKey).map(t => ({ key: t, label: t }))
    CRMHelper.mergeOptionListIntoField(fieldDef, catalog, fieldKey || attrKey)
  }

  /** Copy `fieldDef.options[fieldKey]` onto matching step fields (API often stores catalogs on the map). */
  static hydrateFieldDefSelectOptionsFromMap(fieldDef) {
    if (!fieldDef || !fieldDef.steps || !fieldDef.steps[0] || !Array.isArray(fieldDef.steps[0].fields)) return
    const optionMap = fieldDef.options
    if (!optionMap || typeof optionMap !== 'object') return
    const normalizeOptionMapEntries = (raw) => {
      if (!Array.isArray(raw) || raw.length === 0) return []
      return raw.map((o) => {
        if (o == null) return null
        if (typeof o === 'string') return { key: o, label: o }
        const key = o.key != null ? o.key : (o.id != null ? o.id : o.label)
        if (key == null || key === '') return null
        return {
          key: String(key),
          label: String(o.label != null ? o.label : (o.text != null ? o.text : key)),
        }
      }).filter(Boolean)
    }
    fieldDef.steps[0].fields.forEach((field) => {
      if (!field || !field.key || !['radio', 'check_box', 'select'].includes(field.type)) return
      const mapKeys = [field.key, field._uiKey].filter(Boolean)
      mapKeys.forEach((mapKey) => {
        const normalized = normalizeOptionMapEntries(optionMap[mapKey])
        if (normalized.length) {
          CRMHelper.mergeOptionListIntoField(fieldDef, normalized, field.key)
        }
      })
    })
    CRMHelper.CRM_ACCOUNT_CATALOG_OPTION_KEYS.forEach((uiKey) => {
      const normalized = normalizeOptionMapEntries(optionMap[uiKey])
      if (normalized.length) {
        CRMHelper.mergeOptionListIntoField(fieldDef, normalized, uiKey)
      }
    })
  }

  /** After JSON-cloning a fieldDef, restore select options from the live source fieldDef. */
  static copySelectOptionsFromBaseFieldDef(targetFieldDef, sourceFieldDef) {
    if (!targetFieldDef || !sourceFieldDef) return
    if (!targetFieldDef.steps || !targetFieldDef.steps[0] || !Array.isArray(targetFieldDef.steps[0].fields)) return
    if (!sourceFieldDef.steps || !sourceFieldDef.steps[0] || !Array.isArray(sourceFieldDef.steps[0].fields)) return
    const sourceByKey = {}
    sourceFieldDef.steps[0].fields.forEach((f) => {
      if (f && f.key) sourceByKey[f.key] = f
    })
    targetFieldDef.steps[0].fields.forEach((field) => {
      const src = sourceByKey[field.key]
      if (!src || !['radio', 'check_box', 'select'].includes(field.type)) return
      if (Array.isArray(src.options) && src.options.length > 0) {
        field.options = JSON.parse(JSON.stringify(src.options))
      }
    })
    if (sourceFieldDef.options && typeof sourceFieldDef.options === 'object') {
      targetFieldDef.options = JSON.parse(JSON.stringify(sourceFieldDef.options))
    }
  }

  /** Merge aggregation bucket keys into account form select options (list + ABM stats). */
  static mergeStatsBucketOptionsIntoField(fieldDef, stats, fieldKey) {
    if (!fieldDef || !stats || !fieldKey) return
    const statsKeys = fieldKey.startsWith('account.')
      ? [fieldKey]
      : [fieldKey, `account.${fieldKey}`]
    const buckets = []
    statsKeys.forEach((statsKey) => {
      const agg = stats[statsKey]
      if (agg && Array.isArray(agg.buckets)) {
        buckets.push(...agg.buckets)
      }
    })
    if (!buckets.length) return
    const opts = buckets
      .filter(b => b && b.key != null && b.key !== '')
      .map(b => ({ key: b.key, label: b.label != null ? b.label : b.key }))
    CRMHelper.mergeOptionListIntoField(fieldDef, opts, fieldKey)
  }

  /**
   * Account form selects: industry/city/country from dashboard stats buckets.
   * Account tags use the contact tag catalog (crmcontactattrs / USER_TAGS), not account attrs.
   */
  static enrichCrmAccountFormFieldDefOptions(fieldDef, { stats, crmContactAttrs } = {}) {
    if (!fieldDef) return fieldDef
    CRMHelper.hydrateFieldDefSelectOptionsFromMap(fieldDef)
    if (crmContactAttrs) {
      CRMHelper.mergeAttrOptionsIntoField(fieldDef, crmContactAttrs, 'tags', 'tags')
      CRMHelper.CRM_ACCOUNT_CATALOG_OPTION_KEYS.forEach((key) => {
        CRMHelper.mergeAttrOptionsIntoField(fieldDef, crmContactAttrs, key, key)
      })
    }
    CRMHelper.CRM_ACCOUNT_CATALOG_OPTION_KEYS.forEach((key) => {
      if (stats) {
        CRMHelper.mergeStatsBucketOptionsIntoField(fieldDef, stats, key)
      }
    })
    if (stats && typeof stats === 'object') {
      Object.keys(stats).forEach((statsKey) => {
        const bare = statsKey.startsWith('account.') ? statsKey.slice('account.'.length) : statsKey
        if (CRMHelper.CRM_ACCOUNT_CATALOG_OPTION_KEYS.includes(bare)) {
          CRMHelper.mergeStatsBucketOptionsIntoField(fieldDef, stats, bare)
        }
      })
    }
    const industryField = CRMHelper.findFormField(fieldDef, 'industry')
    if (industryField && industryField.addOptions) {
      industryField.addOptionsFormatter = 'lowercase'
    }
    return fieldDef
  }

  /** Ensure addOptions select fields include the current record value(s) in options (edit dialog). */
  static ensureAddOptionsFieldValues(fieldDef, current) {
    if (!fieldDef || !fieldDef.steps || !fieldDef.steps[0] || !fieldDef.steps[0].fields || !current) return
    fieldDef.steps[0].fields.forEach((field) => {
      if (!field || !field.addOptions || !['radio', 'check_box', 'select'].includes(field.type)) return
      const raw = current[field.key]
      if (raw == null || raw === '') return
      const values = Array.isArray(raw) ? raw : [raw]
      values.forEach((v) => {
        if (v == null || v === '') return
        CRMHelper.mergeOptionListIntoField(fieldDef, [{ key: String(v), label: String(v) }], field.key)
      })
    })
  }

  /** Shared add/edit account dialog fieldDef (same option catalog as Add Account). */
  static prepareCrmAccountModalFieldDef(sourceFieldDef, {
    title,
    loadingMode,
    isMobileView,
    stats,
    crmContactAttrs,
    optionsSourceFieldDef,
  } = {}) {
    const source = sourceFieldDef || { steps: [{ fields: [] }] }
    const optionsSource = optionsSourceFieldDef || source
    const fieldDef = JSON.parse(JSON.stringify(source))
    CRMHelper.copySelectOptionsFromBaseFieldDef(fieldDef, optionsSource)
    if (title) fieldDef.title = title
    if (loadingMode != null) fieldDef.loadingMode = loadingMode
    CRMHelper.enrichCrmAccountFormFieldDefOptions(fieldDef, { stats, crmContactAttrs })
    const fields = (fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields) || []
    fields.forEach((field) => {
      if (!field) return
      delete field.operatorVariants
      delete field.operators
      delete field.selectedOperator
      delete field.defaultOperator
      if (isMobileView && ['radio', 'check_box'].includes(field.type)) {
        field.disableTyping = true
      }
    })
    return fieldDef
  }

  static buildCrmAccountEditFieldDef(baseFieldDef, current, opts = {}) {
    const fieldDef = CRMHelper.prepareCrmAccountModalFieldDef(baseFieldDef, {
      ...opts,
      title: Helper.getString('editAccount'),
      loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD,
    })
    CRMHelper.ensureAddOptionsFieldValues(fieldDef, current)
    return fieldDef
  }

  /**
   * Resolve employeeCount (and other NUMBER range-band) chart bucket to a named-band filter
   * (`account.employeeCount=1 to 10`), same shape as the filter-dialog multi-select.
   */
  static resolveNumberBandChartFilter(chartKey, cell, fieldDef, accountFieldDef) {
    if (!chartKey || cell == null || cell === '') return null
    const uiKey = chartKey.startsWith('account.') ? chartKey.slice('account.'.length) : chartKey
    let field = null
    ;[fieldDef, accountFieldDef].filter(Boolean).some((fd) => {
      const fields = (fd && fd.steps && fd.steps[0] && fd.steps[0].fields) || []
      field = fields.find(f => f && (
        f.key === chartKey || f._uiKey === uiKey || f.key === uiKey
      ))
      return !!field
    })
    if (!field || !Array.isArray(field.options) || field.options.length === 0) return null
    const hasBandOptions = field.options.some(o => o && (o.from != null || o.to != null))
    const isBandField = hasBandOptions && (
      field.type === 'numberRange'
      || field.type === 'check_box'
      || (field.type === 'edit_text' && field.v_float)
    )
    if (!isBandField) return null
    const matched = field.options.find(o => o && (
      String(o.key) === String(cell) || String(o.label) === String(cell)
    ))
    if (!matched || matched.key == null || matched.key === '') return null
    const formKey = chartKey.startsWith('account.') ? chartKey : (field._uiKey || field.key)
    return { [formKey]: [String(matched.key)] }
  }

  /** Apply chart-slice drill-down filter for account chart keys (e.g. account.industry, tags, industry). */
  static applyAccountChartFilter(searchFilters, chartKey, cell, { fieldDef, accountFieldDef } = {}) {
    const filters = { ...(searchFilters || {}) }
    if (!chartKey) return filters
    const filterKey = chartKey
    const gteKey = `gte${Helper.capitalizeFirstLetter(filterKey)}`
    const lteKey = `lte${Helper.capitalizeFirstLetter(filterKey)}`
    delete filters[filterKey]
    delete filters[`${filterKey}!`]
    delete filters[gteKey]
    delete filters[lteKey]
    if (!cell) return filters
    const bandFilter = CRMHelper.resolveNumberBandChartFilter(chartKey, cell, fieldDef, accountFieldDef)
    if (bandFilter) {
      return { ...filters, ...bandFilter }
    }
    filters[filterKey] = [cell]
    return filters
  }

  static statsToPieData(stats, key, label, orgId) {
    if (!stats || !(key in stats) || !stats[key].buckets) return {}
    let buckets = stats[key].buckets
    let labels = [], data = [], backgroundColor = []
    const expandedLabels = {}
    if(key === 'states') {
      const specialBucketKeyOrder = ['BAD TIMING', 'LOST', 'UNQUALIFIED']
      const progressiveBucketKeyOrder = ['PROSPECT', 'NEW', 'OPEN', 'ATTEMPTED TO CONTACT', 'CONNECTED', 'IN PROGRESS', 'OPEN DEAL', 'CUSTOMER']
      const specialBucketKeys = specialBucketKeyOrder.reduce((p, c) => p.concat(buckets.filter(b => b.key === c)), [])
      const progressiveBucketKeys = progressiveBucketKeyOrder.reduce((p, c) => p.concat(buckets.filter(b => b.key === c)), [])
      const sortedBucketKeys = progressiveBucketKeys.concat(specialBucketKeys)
      const numProgressiveBucketKeys = Number(progressiveBucketKeys.length)
      backgroundColor = sortedBucketKeys.map((b, i) => CRMHelper.getPieChartColor(b.key, numProgressiveBucketKeys - 1 - i, numProgressiveBucketKeys))
      labels = sortedBucketKeys.map(b => b.key)
      data = sortedBucketKeys.map(b => b.doc_count)
      labels.forEach((label, index) => {
        label = UiHelper.renameCRMContactLeadStatus(orgId,label)
        if (label === 'ATTEMPTED TO CONTACT') {
          expandedLabels[index] = label
          labels[index] = `ATC (${data[index]})`
        } else {
          labels[index] = `${label} (${data[index]})`
        }
      });
    } else {
      buckets = buckets.filter(b => b.key)
      const shown = Math.min(buckets.length, 10)
      backgroundColor = buckets.map((b, i) => CRMHelper.getPieChartColor(b.key, Math.max(shown - 1 - i, 0), shown))
      data = buckets.map(b => b.doc_count)
      labels = buckets.map(b => b.label ? b.label : b.key)
      labels.forEach((label, index) => {
        labels[index] = `${label} (${data[index]})`
      })
    }
    return {
      keys: buckets.map(b => b.key),
      labels,
      expandedLabels,
      datasets: [
        {
          label,
          data,
          backgroundColor,
          borderWidth: 1,
        },
      ],
    }
  }

  static statsToLineData(stats, key, label, date, timezone) {
    if (!stats || !(key in stats) || !stats[key].buckets || stats[key].buckets.length === 0) return {}
    let buckets = stats[key].buckets

    if (key === 'outreaches_per_day') {
      const lastOutreachBuckets = stats.last_outreaches_per_day && stats.last_outreaches_per_day.buckets
      if (lastOutreachBuckets && lastOutreachBuckets.length > 0) {
        buckets = CRMHelper.mergeBucketsByMaxDocCount(buckets, lastOutreachBuckets)
      }
    }

    const fullData = [];
    let labels, dateTsList
    if(date) {
      if(date.id === 'today') {
        labels = Helper.getHoursIn12HourFormat()
        dateTsList = CRMHelper.getToday24HoursTs(Helper.currentTs(), timezone)
      } else if(date.id === 'yesterday') {
        labels = Helper.getHoursIn12HourFormat()
        dateTsList = CRMHelper.getYesterday24HoursTs(Helper.currentTs(), timezone)
      } else if (date.id === 'this_week') {
        labels = CRMHelper.getDateRangeLabels('this_week', timezone)
        dateTsList = CRMHelper.getDateRangeStartTs('this_week', timezone)
      } else if (date.id === 'this_month') {
        labels = CRMHelper.getDateRangeLabels('this_month', timezone)
        dateTsList = CRMHelper.getDateRangeStartTs('this_month', timezone)
      } else if(date.id === 'last_7d') {
        labels = Helper.generatePreviousMonthsOrDays('days', 7, timezone)
        dateTsList = CRMHelper.getLastDaysStartTs(Helper.currentTs(), 7, timezone)
      } else if(date.id === 'last_30d') {
        labels = Helper.generatePreviousMonthsOrDays('days', 30, timezone)
        dateTsList = CRMHelper.getLastDaysStartTs(Helper.currentTs(), 30, timezone)
      } else if(date.id === 'last_90d') {
        labels = Helper.generatePreviousMonthsOrDays('days', 90, timezone)
        dateTsList = CRMHelper.getLastDaysStartTs(Helper.currentTs(), 90, timezone)
      } else if(date.id === 'last_6m') {
        labels = Helper.generatePreviousMonthsOrDays('months', 6, timezone)
        dateTsList = CRMHelper.getLastMonthsStartTs(Helper.currentTs(), 6, timezone)
      } else if(date.id === 'last_12m') {
        labels = Helper.generatePreviousMonthsOrDays('months', 12, timezone)
        dateTsList = CRMHelper.getLastMonthsStartTs(Helper.currentTs(), 12, timezone)
      } else if (['current_7d', 'current_30d', 'current_90d', 'current_6m', 'current_12m','next_7d'].includes(date.id)) {
        const rangeData = CRMHelper.generateLabelsAndTimestampsFromDateId(date.id, timezone)
        labels = rangeData.labels
        dateTsList = rangeData.dateTsList
      } else if(date.id === 'custom') {
        const customData = CRMHelper.generateCustomLabelsAndTimestamps(date, timezone)
        labels = customData ? customData.labels : []
        dateTsList = customData ? customData.dateTsList : []
      }
    }

    if (!dateTsList || dateTsList.length === 0) return {}

    let isNonZeroCountFound = false
    for(const dateTs of dateTsList) {
      const foundObj = buckets.find(obj => obj.key === dateTs)
      const data = {key: dateTs, doc_count: 0}
      if(foundObj) {
        data.doc_count = foundObj.doc_count
        isNonZeroCountFound = true
      }
      fullData.push(data)
    }

    if(!isNonZeroCountFound) return {}

    let dataPoints = []
    if(date) {
      if(date.id === 'today') {
        const currentHour = moment.tz(timezone).hour();
        dataPoints = fullData.map(b => b.doc_count).slice(0, currentHour + 1)
      } else {
        dataPoints = fullData.map(b => b.doc_count)
      }
    }

    return {
      labels,
      datasets: [
        {
          label,
          data: dataPoints,
          borderWidth: 2,
          borderColor: 'rgba(90, 140, 245, 1)',
          segment: {
            borderDash: ctx => {
              return ((ctx.p1DataIndex === dataPoints.length - 1) && (date && date.id !== 'yesterday')) ? [2,2] : []
            }
          },
        },
      ],
    }
  }

  static statsToBarData(stats, key, label) {
    if (!stats || !(key in stats) || !stats[key].buckets) return {}
    const topBuckets = stats[key].buckets;
    const shown = Math.min(topBuckets.length, 10)
    return {
      keys: topBuckets.map(b => b.key),
      labels: topBuckets.map(b => b.label ? b.label : b.key),
      datasets: [
        {
          label,
          data: topBuckets.map(b => b.doc_count),
          backgroundColor: topBuckets.map((b, i) => CRMHelper.generateColor(Number(Math.max(shown - i, 1)) / Number(shown))),
          borderWidth: 1,
        },
      ],
    }
  }

  static getFilterDialogFieldDef(fields) {
    const updatedFields = fields.map(field => {
      if (field.type === 'radio') {
        field = { ...field, type: 'check_box' };
      }

      if (field.v_required && field.v_required.value === true) {
      // eslint-disable-next-line camelcase
        field.v_required = { ...field.v_required, value: false };
      }
      return field;
    });

    return {
      title: 'All Filters',
      ctaButtonText: 'Apply Filters',
      steps: [
        {
          fields: updatedFields
        }
      ]
    }
  }

  static generateLabelsAndTimestampsFromDateId(dateId, timezone) {
    const tz = timezone || Helper.getLocalTimezone()
    const startDate = FilterHelper.getEpochFromDateId(dateId, 'startDate', tz)
    const endDate = FilterHelper.getEpochFromDateId(dateId, 'endDate', tz)
    if (startDate == null || endDate == null) return { labels: [], dateTsList: [] }

    const labels = []
    const dateTsList = []
    const monthRangeIds = ['last_6m', 'last_12m', 'current_6m', 'current_12m']

    if (monthRangeIds.includes(dateId)) {
      const current = moment.tz(startDate, tz).startOf('month')
      const endMonth = moment.tz(endDate, tz).startOf('month')
      while (current.isSameOrBefore(endMonth)) {
        labels.push(current.format('MMM YYYY'))
        dateTsList.push(current.valueOf())
        current.add(1, 'month')
      }
    } else {
      const current = moment.tz(startDate, tz).startOf('day')
      const end = moment.tz(endDate, tz).startOf('day')
      while (current.isSameOrBefore(end)) {
        labels.push(current.format('MMM DD'))
        dateTsList.push(current.valueOf())
        current.add(1, 'day')
      }
    }

    return { labels, dateTsList }
  }

  static generateCustomLabelsAndTimestamps(date, timezone) {
    const labels = []
    const dateTsList = []
    const tz = timezone || Helper.getLocalTimezone()

    if (date && date.startValue && date.endValue) {
      const startDate = date.startValue
      const endDate = date.endValue
      const countOfDays = CRMHelper.getCountOfDaysInCustomDateRange(date, timezone)
      const start = moment.tz(startDate, tz).startOf('day')
      const end = moment.tz(endDate, tz).startOf('day')

      if (countOfDays === 1) {
        labels.push(...Helper.getHoursIn12HourFormat())
        const current = start.clone()

        for (let i = 0; i < 24; i++) {
          dateTsList.push(current.valueOf())
          current.add(1, 'hour')
        }

      } else if (countOfDays > 90) {
        const current = start.clone().startOf('month')
        const endMonth = end.clone().startOf('month')

        while (current.isSameOrBefore(endMonth)) {
          labels.push(current.format('MMM YYYY'))
          dateTsList.push(current.valueOf())
          current.add(1, 'month')
        }

      } else {
        const current = start.clone()

        while (current.isSameOrBefore(end)) {
          labels.push(current.format('MMM DD'))
          dateTsList.push(current.valueOf())
          current.add(1, 'day')
        }
      }
    }

    return { labels, dateTsList }
  }

  static isMultiValueContainsField(field) {
    if (!field) return false
    const containsVariant = (field.operatorVariants || []).find(v =>
      v && v.operatorCondition && Array.isArray(v.operatorCondition.values)
      && v.operatorCondition.values.includes('contains')
    )
    if (containsVariant) {
      return !!(containsVariant.multiSelect || containsVariant.type === 'check_box')
    }
    return !!(field.multiSelect || field.type === 'check_box')
  }

  static getNormalizedSearchFilters(filterOptions, searchFilters) {
    const normalizedSearchFilters = {}
    filterOptions.forEach(field => {
      const searchFilterKey = FilterHelper.resolveSearchFilterKey(field.key, searchFilters, filterOptions)
      const searchFilterVal = searchFilterKey ? searchFilters[searchFilterKey] : null
      if (searchFilterVal) {
        if (searchFilterKey.endsWith('*')) {
          // Single-value contains (edit_text) keeps first entry; multi-chip contains keeps the full list
          if (CRMHelper.isMultiValueContainsField(field)) {
            normalizedSearchFilters[field.key] = Array.isArray(searchFilterVal)
              ? searchFilterVal
              : [searchFilterVal]
          } else {
            normalizedSearchFilters[field.key] = Array.isArray(searchFilterVal) ? searchFilterVal[0] : searchFilterVal
          }
        } else {
          normalizedSearchFilters[field.key] = searchFilterVal
        }
        if (field.key === 'assignee' || field.key === 'assignee!') {
          normalizedSearchFilters[field.key] = CRMHelper.normalizeAssigneeValue(normalizedSearchFilters[field.key])
        }
      }
    })
    return normalizedSearchFilters
  }

  static getLabelFromDateId(dateId) {
    const dateLabelMap = {
      due: 'Due',
      overdue: 'Overdue',
      upcoming: 'Upcoming',
      next_7d: 'Next 7 days',
      today: 'Today',
      yesterday: 'Yesterday',
      this_week: 'This week',
      this_month: 'This month',
      last_7d: 'Last 7 days',
      last_30d: 'Last 30 days',
      last_90d: 'Last 90 days',
      last_6m: 'Last 6 months',
      last_12m: 'Last 12 months',
      current_7d: 'Current 7 days',
      current_30d: 'Current 30 days',
      current_90d: 'Current 90 days',
      current_6m: 'Current 6 months',
      current_12m: 'Current 12 months',
      custom: 'Custom'
    }
    return dateLabelMap[dateId]
  }

  static getCountOfDaysInCustomDateRange(date, timezone) {
    let countOfDays = 0
    const tz = timezone || Helper.getLocalTimezone()

    if (date && date.startValue && date.endValue) {
      const start = moment.tz(date.startValue, tz).startOf('day')
      const end = moment.tz(date.endValue, tz).startOf('day')
      countOfDays = end.diff(start, 'days') + 1
    }

    return countOfDays
  }

  static getTransformedKey(filterOption) {
    const operator = filterOption.selectedOperator ? filterOption.selectedOperator : filterOption.defaultOperator
    let transformedKey = filterOption.key
    if(['not equals', 'none'].includes(operator)) {
      transformedKey = transformedKey + '!'
    } else if(['all'].includes(operator)) {
      transformedKey = transformedKey + '~'
    } else if(['contains'].includes(operator)) {
      transformedKey = transformedKey + '*'
    }
    return transformedKey
  }

  static getUpdatedSearchFilters(filterOptions, searchFilters) {
    const updatedSearchFilters = {}
    filterOptions.forEach(field => {
      const searchFilterKey = FilterHelper.resolveSearchFilterKey(field.key, searchFilters, filterOptions)
      const searchFilterVal = searchFilterKey ? searchFilters[searchFilterKey] : null
      if(searchFilterVal) {
        const finalKey = CRMHelper.getTransformedKey(field)
        updatedSearchFilters[finalKey] = searchFilterVal
      }
    })
    return updatedSearchFilters
  }

  static getPreviewFromFields(contact, fieldDef, previewFields, displayTs) {
    if (contact && previewFields && previewFields.length > 0 && previewFields.flat().length > 0) {
      const isMobileView = Helper.isMobileView()
      const lines = []

      const groups = Array.isArray(previewFields[0]) ? previewFields : [previewFields]

      groups.forEach(fieldsGroup => {
        const values = []

        fieldsGroup.forEach(field => {
          let value = ''
          let label = ''
          let showLabel = false

          if (field.startsWith('+')) {
            showLabel = true
            field = field.slice(1)
          }

          if (field === 'name') {
            label = 'Name'
            const name = Helper.formatName(contact)
            if(name || contact.friendlyname) {
              value = `${name} ${contact.friendlyname ? `(${contact.friendlyname})` : ''}`
            }
          } else if (field === 'jobDescription') {
            const companyName = contact.friendlycompanyname || contact.company
            if (companyName && contact.jobTitle) {
              label = 'Job Description'
              value = `${contact.jobTitle} @ ${companyName}`
            } else if (contact.jobTitle) {
              label = 'Job Description'
              value = contact.jobTitle
            } else if (companyName) {
              // Company-only: avoid "Job Description" label (cdp-2059); use Company instead
              label = 'Company'
              value = companyName
            }
          } else if (!isMobileView || !['phone', 'createdTs'].includes(field)) {
            const customField = Helper.getCustomField(fieldDef, field)
            if (customField && customField.key && contact[customField.key]) {
              label = customField.label || ''
              if (customField.type === 'date_yyyymmdd') {
                value = moment(contact[customField.key]).format('DD-MMM-YYYY')
              } else if (customField.type === 'dateTime') {
                value = Helper.formatTimestamp(contact[customField.key])
              } else {
                value = contact[customField.key]
              }
            } else if (contact[field]) {
              value = contact[field]
              const regularField = Helper.getField(fieldDef, field)
              if (regularField) {
                label = regularField.label || ''
              } else if(field === 'ltv') {
                label = 'Lifetime Value'
                value = Helper.formatLargeNumber(value || 0)
              } else {
                label = field
              }
            }
          }

          value = Array.isArray(value) ? value.join(', ') : value
          value = label && value && showLabel ? (
            <span>
              <span className={css(Styles.previewLabel)}>{label}:</span> {value}
            </span>
          ) : (value)

          if (value) values.push(value);
        })

        if (values.length > 0) lines.push(values)
      })

      if (isMobileView) {
        let phoneFieldKey = ['+phone', 'phone'].find(f => groups.flat().includes(f))
        let phoneLabel = ''
        if (phoneFieldKey && phoneFieldKey === '+phone') {
          phoneFieldKey = phoneFieldKey.slice(1)
          phoneLabel = 'Mobile Phone'
        }

        return (
          <div className={css(Styles.spanValue, Styles.infoCellMobile)}>
            {lines.map((values, idx) => (
              <span key={idx} className={css(Styles.infoFieldMobile)}>
                {values.map((spanText, i) => (
                  <Fragment key={i}>
                    {spanText}
                    {i < values.length - 1 && ' | '}
                  </Fragment>
                ))}
              </span>
            ))}

            {phoneLabel && phoneFieldKey && contact.phone && (
              <span>
                <span className={css( Styles.previewLabel)}>{phoneLabel}: </span>
                <span
                  className={css(Styles.phoneValue)}
                  onClick={() => Helper.copyToClipboard(contact.phone)}
                >
                  {contact.phone}
                </span>
              </span>
            )}
            {!phoneLabel && phoneFieldKey && contact.phone && (
              <span
                className={css(Styles.phoneValue)}
                onClick={() => Helper.copyToClipboard(contact.phone)}
              >
                {contact.phone}
              </span>
            )}
            <span className={css(Styles.line3)}>
              {Helper.formatTimestamp(displayTs)}
            </span>
          </div>
        )
      }

      return (
        <div className={css(Styles.spanValue)}>
          {lines.map((values, lineIndex) => (
            <div key={lineIndex}>
              {values.map((v, i) => (
                <Fragment key={i}>
                  {v}
                  {i < values.length - 1 && ' | '}
                </Fragment>
              ))}
            </div>
          ))}
        </div>
      )
    }
  }

  static getContactInfoCellSpan(contact) {
    let preSpanText = '', postSpanText = ''
    const name = Helper.formatName(contact)
    if (name) { preSpanText += `${name} ${contact.friendlyname ? `(${contact.friendlyname})` : ''}` }
    if (contact.jobTitle) {
      const companyName = contact.friendlycompanyname || contact.company;
      if (companyName) {
        preSpanText += `${preSpanText ? ' | ' : ''}${contact.jobTitle} @ ${companyName}`;
      } else {
        preSpanText += `${preSpanText ? ' | ' : ''}${contact.jobTitle}`;
      }
    } else if (contact.friendlycompanyname) {
      preSpanText += `${preSpanText ? ' | ' : ''}${contact.friendlycompanyname}`;
    } else if (contact.company) {
      preSpanText += `${preSpanText ? ' | ' : ''}${contact.company}`;
    }
    if (contact.email) preSpanText += `${preSpanText ? ' | ' : ''}${contact.email}`
    if (contact.phone) postSpanText += `${preSpanText ? ' | ' : ''}${contact.phone}`
    return <span className={css(Styles.spanValue)}>{preSpanText}{postSpanText}</span>
  }

  static getContactInfoCellSpanMobile(contact, displayTs) {
    const name = Helper.formatName(contact)
    let companyName = ''
    if (contact.friendlycompanyname) {
      companyName = contact.friendlycompanyname
    } else if (contact.company) {
      companyName = contact.company
    }

    return(
      <div className={css(Styles.spanValue, Styles.infoCellMobile)}>
        <span class={css(Styles.infoFieldMobile)}>{name && name}</span>
        {contact.jobTitle && <span class={css(Styles.infoFieldMobile)}>{contact.jobTitle}</span>}
        <span class={css(Styles.infoFieldMobile)}>{companyName && companyName}</span>
        <span class={css(Styles.infoFieldMobile)}>{contact.email && contact.email}</span>
        {contact.phone && (<span className={css(Styles.phoneValue)} onClick={() => Helper.copyToClipboard(contact.phone)}>{contact.phone}</span>)}
        <span className={css(Styles.line3)}>{Helper.formatTimestamp(displayTs)}</span>
      </div>
    )
  }

  static extractWorkflows(entityRoot) {
    const workflows = {}
    try {
      const {_workflows} = entityRoot
      _workflows.forEach(workflow => {
        const fieldDefKey = `fieldDef${workflow.key}`
        if (fieldDefKey in entityRoot) {
          workflows[workflow.key] = {
            key: workflow.key,
            title: workflow.label,
            _hasWa: workflow._hasWa,
            fieldDef: entityRoot[fieldDefKey]
          }
        }
      })
    } catch (err) {
      console.error('extractWorkflows err: ', err)
    }
    return workflows
  }

  static processFilterOptions(filterOptions, orgId) {
    if (!Array.isArray(filterOptions) || !orgId) {
      return filterOptions
    }

    return filterOptions.map((item) => {
      if (!item || item.key !== 'state' || !Array.isArray(item.options)) {
        return item
      }

      return {
        ...item,
        options: item.options.map((option) => {
          if (!option || !option.label) {
            return option;
          }

          return {
            ...option,
            label: UiHelper.renameCRMContactLeadStatus(orgId, option.label)
          }
        })
      }
    })
  }

  static getUserIdFromEnityId(entityId = null) {
    let userId = entityId
    if (typeof entityId === 'string' && entityId.indexOf('-') !== -1) {
      const parts = entityId.split('-')
      userId = parts[parts.length - 1]
    }
    return userId
  }

  static getCustomFiltersCdpSettingKey(pageKey) {
    if (pageKey === 'crm-contacts') { return 'CRM_CUSTOM_FILTERS' }
    if (pageKey === 'crm-accounts') { return 'CRM_ACCOUNT_CUSTOM_FILTERS' }
    return null
  }

  static serializeCustomFilters(filters) {
    return (filters || []).map(f => `${f.label}: ${f.value}`).join(' | ')
  }

  static customFilterReferencesSubOrgId(filter) {
    if (!filter || !filter.value) {
      return false
    }
    const qs = String(filter.value).replace(/^\?/, '')
    try {
      const params = new URLSearchParams(qs)
      for (const key of params.keys()) {
        if (key === 'subOrgId' || key === 'subOrgId!') {
          return true
        }
      }
    } catch (err) {
      return /\bsubOrgId!?=/i.test(qs)
    }
    return false
  }

  static getCustomFilters(orgId, pageKey, { excludeSubOrgReferencing } = {}) {
    const cdpSettingKey = CRMHelper.getCustomFiltersCdpSettingKey(pageKey)
    if (!orgId || !cdpSettingKey) {
      return null
    }

    let filters = []
    const customFiltersCdpSettings = AppHelper.getSetting(orgId, cdpSettingKey)
    if (customFiltersCdpSettings) {
      filters = customFiltersCdpSettings.split('|')
        .map(part => {
          const [lhs, rhs] = part.split(':')
          return {
            label: lhs.trim(),
            value: rhs.trim().replace(/^\?/, ''),
          }
        })
    }
    if (excludeSubOrgReferencing) {
      filters = filters.filter((f) => !CRMHelper.customFilterReferencesSubOrgId(f))
    }
    return filters
  }

  static getCustomFiltersMaxPerRow(orgId, filterCount) {
    if (!filterCount || filterCount <= 5) {
      return 1
    }
    let maxPerRow = 2
    const maxPerRowSetting = AppHelper.getSetting(orgId, 'CRM_CUSTOM_FILTERS_MAX_PER_ROW')
    if (!isNaN(maxPerRowSetting) && maxPerRowSetting !== '' && maxPerRowSetting > 0) {
      maxPerRow = Number(maxPerRowSetting)
    }
    return maxPerRow
  }

  static deleteCustomFilter({ orgId, pageKey, filter }) {
    const settingsKey = CRMHelper.getCustomFiltersCdpSettingKey(pageKey)
    if (!settingsKey || !orgId || !filter || !filter.label) {
      return Promise.resolve({ success: false })
    }
    const currentSettingString = AppHelper.getSetting(orgId, settingsKey) || ''
    const currentFilters = CRMHelper.getCustomFilters(orgId, pageKey) || []
    const newFilters = currentFilters.filter(f => f.label !== filter.label)
    const newSettingString = CRMHelper.serializeCustomFilters(newFilters)
    return CdpHelper.setSetting({
      orgId,
      settingsKey,
      settingsValue: newSettingString,
      previousKey: settingsKey,
      previousValue: currentSettingString,
    })
  }

  static handleCustomFilterDelete(pageInstance, filter) {
    return CRMHelper.deleteCustomFilter({
      orgId: pageInstance.orgId,
      pageKey: pageInstance.pageKey,
      filter,
    }).then(response => {
      if (response.success) {
        pageInstance.setState({
          showToast: true,
          isErrorToast: false,
          toastMsg: 'Filter deleted',
        })
      } else {
        pageInstance.setState({
          showToast: true,
          isErrorToast: true,
          toastMsg: response.error || 'Failed to delete filter',
        })
      }
      return response
    })
  }

  static getRenameCustomFilterFieldDef(currentLabel) {
    return {
      formType: 'edit',
      title: 'Edit Filter',
      ctaButtonText: 'Save',
      steps: [
        {
          fields: [
            {
              key: 'filterNickname',
              type: 'edit_text',
              label: 'Filter Nickname',
              tip: 'Nickname of the filter',
              value: currentLabel || '',
              v_required: { value: true, err: 'Provide a filter nickname' },
              placement: 'left',
            },
          ],
        },
      ],
    }
  }

  static getCustomFilterDeleteFormDef(filter) {
    return {
      formType: 'delete',
      skipDeleteConfirmInput: true,
      title: filter ? `Delete ${filter.label}` : 'Delete',
      ctaButtonText: Helper.getString('delete'),
      steps: [{ fields: [] }],
    }
  }

  static getCustomFilterActionFormDef(pendingAction) {
    if (!pendingAction || !pendingAction.filter) {
      return CRMHelper.getCustomFilterDeleteFormDef(null)
    }
    if (pendingAction.type === 'edit') {
      return CRMHelper.getRenameCustomFilterFieldDef(pendingAction.filter.label)
    }
    return CRMHelper.getCustomFilterDeleteFormDef(pendingAction.filter)
  }

  static renameCustomFilter({ orgId, pageKey, filter, newLabel }) {
    const settingsKey = CRMHelper.getCustomFiltersCdpSettingKey(pageKey)
    if (!settingsKey || !orgId || !filter || !filter.label) {
      return Promise.resolve({ success: false })
    }
    const trimmedNewLabel = (newLabel || '').trim()
    if (!trimmedNewLabel) {
      return Promise.resolve({ success: false, error: 'Provide a filter nickname' })
    }
    if (trimmedNewLabel === filter.label) {
      return Promise.resolve({ success: true })
    }
    const currentFilters = CRMHelper.getCustomFilters(orgId, pageKey) || []
    const duplicate = currentFilters.find(f => f.label === trimmedNewLabel && f.label !== filter.label)
    if (duplicate) {
      return Promise.resolve({ success: false, error: 'A filter with this name already exists' })
    }
    const filterFound = currentFilters.some(f => f.label === filter.label)
    if (!filterFound) {
      return Promise.resolve({ success: false, error: 'Filter not found' })
    }
    const currentSettingString = AppHelper.getSetting(orgId, settingsKey) || ''
    const newFilters = currentFilters.map(f => (
      f.label === filter.label ? { label: trimmedNewLabel, value: f.value } : f
    ))
    const newSettingString = CRMHelper.serializeCustomFilters(newFilters)
    return CdpHelper.setSetting({
      orgId,
      settingsKey,
      settingsValue: newSettingString,
      previousKey: settingsKey,
      previousValue: currentSettingString,
    })
  }

  static handleCustomFilterRename(pageInstance, filter, newLabel) {
    return CRMHelper.renameCustomFilter({
      orgId: pageInstance.orgId,
      pageKey: pageInstance.pageKey,
      filter,
      newLabel,
    }).then(response => {
      if (response.success) {
        pageInstance.setState({
          showToast: true,
          isErrorToast: false,
          toastMsg: 'Filter renamed',
        })
      } else {
        pageInstance.setState({
          showToast: true,
          isErrorToast: true,
          toastMsg: response.error || 'Failed to rename filter',
        })
      }
      return response
    })
  }

  static getCustomFilterMenuItems(filter, setPendingAction) {
    const actions = []
    if (!CRMHelper.canManageCustomFilters()) {
      return actions
    }
    const menuItems = [
      {
        key: filter.label + '-edit',
        title: 'Edit',
        icon: 'edit',
        action: () => setPendingAction({ type: 'edit', filter }),
      },
      {
        key: filter.label + '-delete',
        title: 'Delete',
        icon: 'delete',
        action: () => setPendingAction({ type: 'delete', filter }),
      },
    ]
    menuItems.forEach((item) => {
      actions.push(UiHelper.actionButton(item))
    })
    return actions
  }

  static canManageCustomFilters() {
    return Controller.get().userMgr().isProductSU() || Controller.get().roleHelper().hasCurrentOrgRole('SUPERUSER')
  }

  static FETCH_POLICY_LABELS = {
    ONLY_REPLIES: 'ONLY REPLIES',
    ALL_KNOWN_CONTACTS: 'KNOWN CONTACTS',
    ALL: 'ALL INBOUND',
  }

  static INBOX_ERROR_MESSAGES = Helper.INBOX_ERROR_FALLBACKS

  static getInboxCategories(inbox) {
    if (inbox && Array.isArray(inbox.category) && inbox.category.length > 0) {
      return inbox.category
    }
    return ['MARKETING', 'CRM']
  }

  static isInboxLive(inbox) {
    return !!(inbox && inbox.state !== 'DISABLED')
  }

  static isWaQuickMessageEnabled(orgId) {
    return AppHelper.isSettingEnabled(orgId, 'WA_QUICK_MESSAGE')
  }

  static getWaSendMessageActionTitle(orgId) {
    return Helper.getString(
      CRMHelper.isWaQuickMessageEnabled(orgId) ? 'sendQuickMessage' : 'sendTemplateMessage'
    )
  }

  static isInboxConnected(inbox) {
    if (!inbox) return false
    return inbox.smtpConnectionState !== 'DISCONNECTED' && inbox.imapConnectionState !== 'DISCONNECTED'
  }

  static hasInboxMarketing(inbox) {
    return CRMHelper.getInboxCategories(inbox).includes('MARKETING')
  }

  static hasInboxCrm(inbox) {
    return CRMHelper.getInboxCategories(inbox).includes('CRM')
  }

  static getInboxHealthIndicator(inbox) {
    if (!CRMHelper.isInboxConnected(inbox)) {
      return { color: '#d32f2f', tooltip: 'Disconnected', key: 'DISCONNECTED' }
    }
    if (inbox && inbox.healthState === 'RECOVERY') {
      return { color: '#f9a825', tooltip: 'Recovery', key: 'RECOVERY' }
    }
    return { color: '#2e7d32', tooltip: 'Healthy', key: 'HEALTHY' }
  }

  /** Maps HEALTHY | RECOVERY | DISCONNECTED (event status strings) to indicator style. */
  static getHealthStatusStyle(status) {
    switch (status) {
    case 'DISCONNECTED':
      return { color: '#d32f2f', tooltip: 'Disconnected', key: 'DISCONNECTED', label: 'DISCONNECTED' }
    case 'RECOVERY':
      return { color: '#f9a825', tooltip: 'Recovery', key: 'RECOVERY', label: 'RECOVERY' }
    case 'HEALTHY':
      return { color: '#2e7d32', tooltip: 'Healthy', key: 'HEALTHY', label: 'HEALTHY' }
    default:
      return {
        color: '#9e9e9e',
        tooltip: status || 'Unknown',
        key: status || 'UNKNOWN',
        label: status || '—',
      }
    }
  }

  static INBOX_HEALTH_HISTORY_EVENT = 'slixta:inbox-health-history'

  static openInboxHealthHistory({ orgId, inboxId, email } = {}) {
    if (!orgId || !inboxId || typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent(CRMHelper.INBOX_HEALTH_HISTORY_EVENT, {
      detail: { orgId, inboxId, email: email || '' },
    }))
  }

  static async fetchInboxHealthHistory({ orgId, inboxId, nextPage, limit = 50 } = {}) {
    if (!orgId || !inboxId) {
      return { items: [], nextPage: null }
    }
    const { ApiHelper } = require('../common/helpers/ApiHelper')
    const { AppConfig } = require('../AppConfig')
    const jsonBody = {
      groupId: 'INBOX',
      iType: 'HEALTH',
      orgId,
      inboxId,
      limit,
    }
    if (nextPage) {
      jsonBody.nextPage = nextPage
    }
    return ApiHelper.callAwait({
      ms: AppConfig.CDP_MS.ENDPOINT,
      method: 'POST',
      paths: ['miscs', 'search-tsanalytic'],
      queryParams: { orgId },
      jsonBody,
    }) || { items: [], nextPage: null }
  }

  static getFetchPolicyLabel(fetchPolicy) {
    if (!fetchPolicy) return ''
    return CRMHelper.FETCH_POLICY_LABELS[fetchPolicy] || String(fetchPolicy).replace(/_/g, ' ')
  }

  static applyFetchPolicyFieldLabels(fieldDef) {
    if (!fieldDef || !fieldDef.steps) return fieldDef
    fieldDef.steps.forEach((step) => {
      (step.fields || []).forEach((field) => {
        if (field.key !== 'fetchPolicy' || !Array.isArray(field.options)) return
        field.options = field.options.map((opt) => {
          const key = opt.key || opt.value
          const label = CRMHelper.FETCH_POLICY_LABELS[key]
          if (!label) return opt
          return { ...opt, label, text: label }
        })
      })
    })
    return fieldDef
  }

  static filterInboxes(inboxes, mode) {
    const list = Array.isArray(inboxes) ? inboxes : []
    return list.filter((inbox) => {
      if (!inbox) return false
      const live = CRMHelper.isInboxLive(inbox)
      const connected = CRMHelper.isInboxConnected(inbox)
      switch (mode) {
      case 'enrolBulk':
        return live && connected && CRMHelper.hasInboxMarketing(inbox)
      case 'enrolSingle':
      case 'send':
        return live && connected && CRMHelper.hasInboxCrm(inbox)
      case 'sendOrEnrol':
        return live && connected
      case 'trigger':
        return live && connected && inbox.sharedInbox === 'YES'
      default:
        return live && connected
      }
    })
  }

  static toInboxSelectOptions(inboxes) {
    return (Array.isArray(inboxes) ? inboxes : []).map((inbox) => {
      const health = CRMHelper.getInboxHealthIndicator(inbox)
      return {
        key: inbox.id,
        label: inbox.email || inbox.id,
        inboxId: inbox.id,
        inboxEmail: inbox.email || '',
        healthColor: health.color,
        healthTooltip: health.tooltip,
      }
    })
  }

  static getInboxErrorMessage(code) {
    if (!code) return null
    return CRMHelper.INBOX_ERROR_MESSAGES[code] || null
  }

  static applyInboxListingFilters(inboxes, filters = {}) {
    const list = Array.isArray(inboxes) ? inboxes : []
    const {
      state,
      category,
      healthState,
      fetchPolicy,
      searchText,
    } = filters || {}
    const q = (searchText || '').trim().toLowerCase()

    return list.filter((inbox) => {
      if (!inbox) return false
      if (state && inbox.state !== state) return false

      if (category) {
        const cats = CRMHelper.getInboxCategories(inbox)
        if (category === 'both') {
          if (!(cats.includes('MARKETING') && cats.includes('CRM'))) return false
        } else if (!cats.includes(category)) {
          return false
        }
      }

      if (healthState) {
        const indicator = CRMHelper.getInboxHealthIndicator(inbox)
        if (indicator.key !== healthState) return false
      }

      if (fetchPolicy && inbox.fetchPolicy !== fetchPolicy) return false

      if (q) {
        const email = (inbox.email || '').toLowerCase()
        const senderName = (inbox.senderName || '').toLowerCase()
        if (!email.includes(q) && !senderName.includes(q)) return false
      }

      return true
    })
  }

  static addTemplateValues(customFields, entityValues, templateValues, prefix, isHumanReadable = false, timeZone) {
    const zone = timeZone || AppConfig.DEFAULT_ZONEINFO
    Object.values((customFields && customFields.items) || {}).forEach(f => {
      if (f.dbKey in entityValues) {
        let v = entityValues[f.dbKey]
        if (isHumanReadable) {
          if (f.iType === 'DATETIME' && v != null && v !== '') {
            const n = Number.parseInt(v, 10)
            if (Number.isFinite(n)) {
              v = Helper.formatDate2({ utcTimestamp: n, zone, format: 'Do MMM YYYY, h:mm a' })
            }
          } else if (f.iType === 'DATE_YYYYMMDD' && v != null && v !== '') {
            const m = moment(String(v), 'YYYYMMDD', true)
            v = m.isValid() ? m.format('DD-MMM-YYYY') : String(v)
          } else if (f.iType === 'SELECT_MULTI' && Array.isArray(v)) {
            v = Helper.joinWithAnd(v)
          }
        }
        templateValues[`${prefix}${prefix ? '.' : ''}${f.uiKey}`] = v
      }
    })
  }

  static addCrmContactTemplateValues(templateValues, crmContact, prefix = '', customFields = {}, isHumanReadable = false, fieldDef = null) {
    Object.assign(templateValues, {
      [`${prefix}${prefix ? '.' : ''}name`]: Helper.formatName(crmContact),
      [`${prefix}${prefix ? '.' : ''}givenname`]: crmContact.givenname || '',
      [`${prefix}${prefix ? '.' : ''}firstname`]: crmContact.givenname || '',
      [`${prefix}${prefix ? '.' : ''}middlename`]: crmContact.middlename || '',
      [`${prefix}${prefix ? '.' : ''}familyname`]: crmContact.familyname || '',
      [`${prefix}${prefix ? '.' : ''}lastname`]: crmContact.familyname || '',
      [`${prefix}${prefix ? '.' : ''}email`]: crmContact.email || '',
      [`${prefix}${prefix ? '.' : ''}phone`]: crmContact.phone || '',
      [`${prefix}${prefix ? '.' : ''}company`]: crmContact.company || '',
      [`${prefix}${prefix ? '.' : ''}friendlyname`]: crmContact.friendlyname || Helper.formatName(crmContact),
      [`${prefix}${prefix ? '.' : ''}friendlycompanyname`]: crmContact.friendlycompanyname || crmContact.company || '',
    })
    if (fieldDef) {
      CRMHelper.addCrmContactFieldDefTemplateValues(templateValues, crmContact, fieldDef, prefix, isHumanReadable)
    } else if (Object.keys(customFields).length > 0) {
      CRMHelper.addTemplateValues(customFields, crmContact, templateValues, prefix, isHumanReadable, crmContact && crmContact.timeZone)
    }
    return templateValues
  }

  static addCrmContactFieldDefTemplateValues(templateValues, crmContact, fieldDef, prefix = 'contact', isHumanReadable = false) {
    const fields = (fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields) || []
    const zone = (crmContact && crmContact.timeZone) || AppConfig.DEFAULT_ZONEINFO
    fields.forEach((f) => {
      if (!f || !f._uiKey || f._uiKey === 'custom') return
      if (!f.key || !(f.key in crmContact)) return
      let v = crmContact[f.key]
      if (isHumanReadable) {
        const fieldType = String(f.type || f.iType || '').toLowerCase()
        if (fieldType === 'datetime' && v != null && v !== '') {
          const n = Number.parseInt(v, 10)
          if (Number.isFinite(n)) {
            v = Helper.formatDate2({ utcTimestamp: n, zone, format: 'Do MMM YYYY, h:mm a' })
          }
        } else if (fieldType === 'date_yyyymmdd' && v != null && v !== '') {
          const m = moment(String(v), 'YYYYMMDD', true)
          v = m.isValid() ? m.format('DD-MMM-YYYY') : String(v)
        } else if ((fieldType === 'check_box' || fieldType === 'select_multi') && Array.isArray(v)) {
          v = Helper.joinWithAnd(v)
        }
      }
      templateValues[`${prefix}${prefix ? '.' : ''}${f._uiKey}`] = v
    })
    return templateValues
  }

  static buildNonEmptyContactTemplateValues(crmContact, fieldDef = null, prefix = 'contact') {
    const all = CRMHelper.addCrmContactTemplateValues({}, crmContact, prefix, {}, true, fieldDef)
    const out = {}
    Object.keys(all).forEach((k) => {
      const v = all[k]
      if (v != null && String(v).trim() !== '') out[k] = v
    })
    return out
  }

  static substituteContactPlaceholders(text, templateValues) {
    return String(text || '').replace(/\{\{([a-zA-Z][a-zA-Z0-9_().]*)\s*(,\s*([^{}]*))?\}\}/g, (full, key) => {
      if (key && Object.prototype.hasOwnProperty.call(templateValues, key)) {
        return templateValues[key]
      }
      return full
    })
  }

  static substituteContactRichText(richText, templateValues) {
    try {
      const raw = typeof richText === 'string' ? richText : JSON.stringify(richText)
      const richTextCopy = JSON.parse(raw)
      richTextCopy.__azformatted.blocks = (richTextCopy.__azformatted.blocks || []).map((block) => {
        const blockCopy = JSON.parse(JSON.stringify(block))
        blockCopy.text = CRMHelper.substituteContactPlaceholders(blockCopy.text, templateValues)
        return blockCopy
      })
      Object.values(richTextCopy.__azformatted.entityMap || {}).forEach((e) => {
        if (e.data && e.data.url) {
          e.data.url = CRMHelper.substituteContactPlaceholders(e.data.url, templateValues)
        }
      })
      return JSON.stringify(richTextCopy)
    } catch (err) {
      return richText || ''
    }
  }

  static collectWaContentVariableTexts(content) {
    if (!content) return []
    const texts = []
    if (content.bodyText != null && content.bodyText !== '') {
      texts.push(String(content.bodyText))
    }
    if (content.bodyRichText) {
      const plain = WhatsappUtils.waRichTextToWhatsAppBodyText(content.bodyRichText)
      if (plain) texts.push(plain)
    }
    const vars = Array.isArray(content.components)
      ? content.components
      : (content.components && typeof content.components === 'object' ? Helper.dictToArray(content.components) : [])
    vars.forEach((v) => {
      if (v && v.text != null && String(v.text).trim() !== '') {
        texts.push(String(v.text))
      }
    })
    return texts
  }

  static getMissingContactPlaceholders(texts, templateValues) {
    const missing = []
    const seen = new Set()
    ;(texts || []).forEach((text) => {
      String(text || '').replace(/\{\{(contact\.[a-zA-Z0-9_().]*)\s*(?:,\s*([^{}]*))?\}\}/g, (full, key) => {
        if (seen.has(key)) return
        seen.add(key)
        const val = templateValues[key]
        if (val == null || String(val).trim() === '') {
          missing.push(`{{${key}}}`)
        }
      })
    })
    return missing
  }

  static generateUTMFields(crmContactsStats) {
    const fields = []
    const operators = [{ key: 'equals', label: 'equals' },{ key: 'not equals', label: 'not equals' }]
    const _url = new URL(document.location.href)
    let utmSourcesOptions = []

    if(crmContactsStats && crmContactsStats.utm_sources && crmContactsStats.utm_sources.buckets && crmContactsStats.utm_sources.buckets.length > 0) {
      utmSourcesOptions = crmContactsStats.utm_sources.buckets.map(bucket => bucket.key).filter(a => !!a).map(item => ({key: item, label: item}))
    }
    fields.push({
      key: 'utmSource',
      type: 'radio',
      label: 'UTM Source',
      options: utmSourcesOptions,
      // eslint-disable-next-line camelcase
      v_required: { value: false, err: 'Provide UTM Source' },
      placement: 'left',
      operators: operators,
      defaultOperator: 'equals',
      multiSelect: true,
    })

    let utmMediumOptions = []
    if(crmContactsStats && crmContactsStats.utm_mediums && crmContactsStats.utm_mediums.buckets && crmContactsStats.utm_mediums.buckets.length > 0) {
      utmMediumOptions = crmContactsStats.utm_mediums.buckets.map(bucket => bucket.key).filter(a => !!a).map(item => ({key: item, label: item}))
    }
    fields.push({
      key: 'utmMedium',
      type: 'radio',
      label: 'UTM Medium',
      options: utmMediumOptions,
      // eslint-disable-next-line camelcase
      v_required: { value: false, err: 'Provide UTM Medium' },
      placement: 'right',
      operators: operators,
      defaultOperator: 'equals',
      multiSelect: true,
    })

    let utmCampaignOptions = []
    if(crmContactsStats && crmContactsStats.utm_campaigns && crmContactsStats.utm_campaigns.buckets && crmContactsStats.utm_campaigns.buckets.length > 0) {
      utmCampaignOptions = crmContactsStats.utm_campaigns.buckets.map(bucket => bucket.key).filter(a => !!a).map(item => ({key: item, label: item}))
    }
    fields.push({
      key: 'utmCampaign',
      type: 'radio',
      label: 'UTM Campaign',
      options: utmCampaignOptions,
      // eslint-disable-next-line camelcase
      v_required: { value: false, err: 'Provide UTM Campaign' },
      placement: 'left',
      operators: operators,
      defaultOperator: 'equals',
      multiSelect: true,
    })

    let utmContentOptions = []
    if(crmContactsStats && crmContactsStats.utm_contents && crmContactsStats.utm_contents.buckets && crmContactsStats.utm_contents.buckets.length > 0) {
      utmContentOptions = crmContactsStats.utm_contents.buckets.map(bucket => bucket.key).filter(a => !!a).map(item => ({key: item, label: item}))
    }
    fields.push({
      key: 'utmContent',
      type: 'radio',
      label: 'UTM Content',
      options: utmContentOptions,
      // eslint-disable-next-line camelcase
      v_required: { value: false, err: 'Provide UTM Content' },
      placement: 'right',
      operators: operators,
      defaultOperator: 'equals',
      multiSelect: true,
    })

    let utmTermOptions = []
    if(crmContactsStats && crmContactsStats.utm_terms && crmContactsStats.utm_terms.buckets && crmContactsStats.utm_terms.buckets.length > 0) {
      utmTermOptions = crmContactsStats.utm_terms.buckets.map(bucket => bucket.key).filter(a => !!a).map(item => ({key: item, label: item}))
    }
    fields.push({
      key: 'utmTerm',
      type: 'radio',
      label: 'UTM Term',
      options: utmTermOptions,
      // eslint-disable-next-line camelcase
      v_required: { value: false, err: 'Provide UTM Term' },
      placement: 'left',
      operators: operators,
      defaultOperator: 'equals',
      multiSelect: true,
    })

    let utmIdOptions = []
    if(crmContactsStats && crmContactsStats.utm_ids && crmContactsStats.utm_ids.buckets && crmContactsStats.utm_ids.buckets.length > 0) {
      utmIdOptions = crmContactsStats.utm_ids.buckets.map(bucket => bucket.key).filter(a => !!a).map(item => ({key: item, label: item}))
    }
    fields.push({
      key: 'utmId',
      type: 'radio',
      label: 'UTM ID',
      options: utmIdOptions,
      // eslint-disable-next-line camelcase
      v_required: { value: false, err: 'Provide UTM ID' },
      placement: 'left',
      operators: operators,
      defaultOperator: 'equals',
      multiSelect: true,
      disabled: _url.searchParams.has('_labelForUtmId') ? true : false
    })

    let slxAdIdOptions = []
    if(crmContactsStats && crmContactsStats.slx_ad_ids && crmContactsStats.slx_ad_ids.buckets && crmContactsStats.slx_ad_ids.buckets.length > 0) {
      slxAdIdOptions = crmContactsStats.slx_ad_ids.buckets.map(bucket => bucket.key).filter(a => !!a).map(item => ({key: item, label: item}))
    }
    fields.push({
      key: 'slxAdId',
      type: 'radio',
      label: 'Ad ID',
      options: slxAdIdOptions,
      // eslint-disable-next-line camelcase
      v_required: { value: false, err: 'Provide Ad ID' },
      placement: 'right',
      operators: operators,
      defaultOperator: 'equals',
      multiSelect: true,
      disabled: _url.searchParams.has('_labelForSlxAdId') ? true : false
    })

    return fields
  }

  static getUtmFields(crmContactsStats) {
    return CRMHelper.generateUTMFields(crmContactsStats) || []
  }

  static appendUtmFields(fieldDef, crmContactsStats) {
    if (!fieldDef || !fieldDef.steps || !fieldDef.steps[0] || !Array.isArray(fieldDef.steps[0].fields)) {
      return fieldDef
    }
    const existing = new Set(fieldDef.steps[0].fields.map((field) => field && (field._uiKey || field.key)).filter(Boolean))
    const toAdd = CRMHelper.getUtmFields(crmContactsStats).filter((field) => field && field.key && !existing.has(field.key))
    if (toAdd.length > 0) {
      fieldDef.steps[0].fields = [...fieldDef.steps[0].fields, ...toAdd]
    }
    return fieldDef
  }

  static getContactInfoExtraFields({ isLeadRoutingEnabled } = {}) {
    const fields = [
      {
        key: 'sessionLandingPage',
        type: 'edit_text',
        label: 'Session Landing Page',
        v_required: { value: false, err: 'Provide Session Landing Page' },
        placement: 'left',
      },
      {
        key: 'originalLandingPage',
        type: 'edit_text',
        label: 'Original Landing Page',
        v_required: { value: false, err: 'Provide Original Landing Page' },
        placement: 'right',
      },
    ]

    if (isLeadRoutingEnabled) {
      fields.push(
        {
          key: 'routedAssignee',
          type: 'radio',
          label: 'Routed Assignee',
          v_required: { value: false, err: 'Provide Routed Assignee' },
          placement: 'left',
          advanced: true,
        },
        {
          key: 'routedSubOrgId',
          type: 'radio',
          label: 'Routed Team',
          v_required: { value: false, err: 'Provide Routed Team' },
          placement: 'right',
          advanced: true,
        }
      )
    }

    ;(CdpHelper.CRM_CONTACT_FIELD_MAPPING_BASE_OPTIONS || []).forEach((item, index) => {
      if (!item || !item.key) return
      fields.push({
        key: item.key,
        type: 'edit_text',
        label: item.label || item.key,
        v_required: { value: false, err: `Provide ${item.label || item.key}` },
        placement: index % 2 === 0 ? 'left' : 'right',
      })
    })

    fields.push(
      {
        key: 'createdTs',
        type: 'dateTime',
        label: 'Created Date',
        v_required: { value: false, err: 'Provide created date' },
        placement: 'left',
        advanced: true,
      },
      {
        key: 'modifiedTs',
        type: 'dateTime',
        label: 'Modified Date',
        v_required: { value: false, err: 'Provide modified date' },
        placement: 'right',
        advanced: true,
      },
      {
        key: 'createdBy',
        type: 'radio',
        label: 'Created By',
        options: [
          { key: 'ADD', label: 'MANUAL ADD' },
          { key: 'IMPORT', label: 'IMPORT' },
          { key: 'FORM', label: 'FORM' },
          { key: 'API', label: 'API' },
        ],
        v_required: { value: false, err: 'Provide created by' },
        placement: 'left',
        advanced: true,
      },
      {
        key: 'emailProvider',
        type: 'radio',
        label: 'Email Provider',
        options: [
          { key: 'GMAIL', label: 'GMAIL' },
          { key: 'OUTLOOK', label: 'OUTLOOK' },
          { key: 'ZOHO', label: 'ZOHO' },
        ],
        v_required: { value: false, err: 'Provide email provider' },
        placement: 'right',
        advanced: true,
      },
      {
        key: 'importJobId',
        type: 'edit_text',
        label: 'Import Job',
        v_required: { value: false, err: 'Provide Import Job Id' },
        placement: 'left',
        advanced: true,
      },
      {
        key: 'lastIntSentTs',
        type: 'dateTime',
        label: 'Latest Activity',
        v_required: { value: false, err: 'Provide Latest Activity' },
        placement: 'right',
        advanced: true,
      },
      {
        key: 'lastActiveIntSentStatus',
        type: 'radio',
        label: 'Latest Activity Status',
        options: [
          { key: 'READ', label: 'READ' },
          { key: 'UNREAD', label: 'UNREAD' },
        ],
        v_required: { value: false, err: 'Provide latest activity status' },
        placement: 'left',
        advanced: true,
      },
      {
        key: 'lastWABroadcastTs',
        type: 'dateTime',
        label: 'Last WA Broadcast Date',
        v_required: { value: false, err: 'Provide Last WA Broadcast Date' },
        placement: 'right',
        advanced: true,
      },
      {
        key: 'lastEmailBroadcastTs',
        type: 'dateTime',
        label: 'Last Email Broadcast Date',
        v_required: { value: false, err: 'Provide Last Email Broadcast Date' },
        placement: 'left',
        advanced: true,
      },
      {
        key: 'lastDripSequenceTs',
        type: 'dateTime',
        label: 'Last Drip Sequence Date',
        v_required: { value: false, err: 'Provide Last Drip Sequence Date' },
        placement: 'right',
        advanced: true,
      }
    )

    return fields
  }

  static appendContactInfoDisplayFields(fieldDef, { isLeadRoutingEnabled, crmContactsStats } = {}) {
    if (!fieldDef || !fieldDef.steps || !fieldDef.steps[0] || !Array.isArray(fieldDef.steps[0].fields)) {
      return fieldDef
    }
    const existing = new Set(fieldDef.steps[0].fields.map((field) => field && (field._uiKey || field.key)).filter(Boolean))
    CRMHelper.appendUtmFields(fieldDef, crmContactsStats)
    const present = new Set(fieldDef.steps[0].fields.map((field) => field && (field._uiKey || field.key)).filter(Boolean))
    const toAdd = CRMHelper.getContactInfoExtraFields({ isLeadRoutingEnabled }).filter((field) => field && field.key && !present.has(field.key))
    if (toAdd.length > 0) {
      fieldDef.steps[0].fields = [...fieldDef.steps[0].fields, ...toAdd]
    }
    fieldDef.steps[0].fields.forEach((field) => {
      const key = field && (field._uiKey || field.key)
      if (key && !existing.has(key)) {
        field.advanced = true
      }
    })
    return fieldDef
  }

  static isHttpUrl(value) {
    if (typeof value !== 'string') {
      return false
    }
    const trimmed = value.trim()
    if (!trimmed || /\s/.test(trimmed) || !/^https?:\/\//i.test(trimmed)) {
      return false
    }
    try {
      const parsed = new URL(trimmed)
      return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
      return false
    }
  }

  static markTextFieldsAsHyperlinks(fieldDef, item) {
    const fields = fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields
    if (!Array.isArray(fields) || !item) {
      return fieldDef
    }
    fields.forEach((field) => {
      if (!field || field.subType || field.html || field.multiline) {
        return
      }
      if (field.type !== 'edit_text') {
        return
      }
      if (CRMHelper.isHttpUrl(item[field.key])) {
        field.subType = 'hyperlink'
      }
    })
    return fieldDef
  }

  // Merge accountFieldDef into contact Info/edit fieldDef as an Accounts section (before Advanced).
  static appendAccountInfoFieldsToContactFieldDef(fieldDef, accountFieldDef) {
    if (!fieldDef || !fieldDef.steps || !fieldDef.steps[0] || !Array.isArray(fieldDef.steps[0].fields)) {
      return fieldDef
    }
    if (!accountFieldDef || !accountFieldDef.steps || !accountFieldDef.steps[0] || !Array.isArray(accountFieldDef.steps[0].fields)) {
      return fieldDef
    }
    const existingKeys = new Set(fieldDef.steps[0].fields.map((f) => f && f.key).filter(Boolean))
    const toAdd = accountFieldDef.steps[0].fields
      .filter((f) => f && f.key && !existingKeys.has(f.key))
      .map((f) => {
        const field = JSON.parse(JSON.stringify(f))
        field.accounts = true
        field.advanced = false
        if (field.v_required) {
          field.v_required = { ...field.v_required, value: false }
        }
        if (['websiteUrl', 'linkedinCompanyUrl'].includes(field._uiKey) || /Url$/i.test(field._uiKey || '')) {
          field.subType = 'hyperlink'
        }
        return field
      })
    if (toAdd.length > 0) {
      fieldDef.steps[0].fields = [...fieldDef.steps[0].fields, ...toAdd]
    }
    return fieldDef
  }

  static async fetchCrmContactsStats({ orgId, subOrgId, aggTermsLimit = 100 } = {}) {
    if (!orgId) {
      return null
    }
    const result = await ApiHelper.callAwait({
      ms: AppConfig.CDP_MS.ENDPOINT,
      method: 'GET',
      paths: ['crmcontacts'],
      queryParams: {
        orgId,
        ...AppHelper.getTeamApiParams(orgId),
        limit: 1,
        _datetimezone: Helper.getLocalTimezone(),
        _dateinterval: '1d',
        _aggTermsLimit: aggTermsLimit,
        'tags!': 'INTERNAL',
      },
    })
    return (result && result.stats) || null
  }

  static async ensureCrmContactsStats({ orgId, subOrgId, crmContactsStats } = {}) {
    if (crmContactsStats) {
      return crmContactsStats
    }
    if (!AppHelper.isCrmContactEditUtmCampaignEnabled(orgId)) {
      return null
    }
    return CRMHelper.fetchCrmContactsStats({ orgId, subOrgId })
  }

  static buildCrmContactEditFieldDef(baseFieldDef, { orgId, isMobileView, crmContactsStats } = {}) {
    const fieldDef = JSON.parse(JSON.stringify(baseFieldDef))
    fieldDef.title = Helper.getString('editContact')
    fieldDef.loadingMode = ApiHelper.LOADING_MODE.INSTANT_LOAD
    if(fieldDef && fieldDef.steps && fieldDef.steps.length > 0 && fieldDef.steps[0].fields) {
      if(AppHelper.isCrmContactEditUtmCampaignEnabled(orgId)) {
        const utmFields = CRMHelper.generateUTMFields(crmContactsStats) || []
        const utmCampaignField = utmFields.find(field => field.key === 'utmCampaign')
        if(utmCampaignField && !Helper.getField(fieldDef, 'utmCampaign')) {
          utmCampaignField.advanced = true
          fieldDef.steps[0].fields.push(utmCampaignField)
        }
      }
      fieldDef.steps[0].fields.forEach((field)=>{
        if(isMobileView && ['radio','check_box'].includes(field.type)) {
          field.disableTyping = true
        }
        if(field.key === 'state') {
          UiHelper.removeHiddenCrmContactStates(field, orgId)
          UiHelper.renameLeadStatusFieldOptions(field, orgId)
        }
      })
    }
    return UiHelper.reflowAdvancedFields(fieldDef)
  }
}

const customFilterNestedDialogProps = {
  fullWidthDialog: false,
  maxWidth: 'xs',
  nestedDialog: true,
}

function handleCustomFilterActionSubmit(options, pendingAction, props, clearAction) {
  const { caller, formData } = options
  const filter = pendingAction && pendingAction.filter
  if (!filter || !pendingAction) {
    caller.setState({ inProcess: false })
    return
  }
  const submitPromise = pendingAction.type === 'edit'
    ? (props.onCustomFilterRename
      ? props.onCustomFilterRename(filter, formData.filterNickname)
      : Promise.resolve({ success: false }))
    : (props.onCustomFilterDelete
      ? props.onCustomFilterDelete(filter)
      : Promise.resolve({ success: false }))
  const failureMessage = pendingAction.type === 'edit' ? 'Failed to rename filter' : 'Failed to delete filter'
  return Promise.resolve(submitPromise)
    .then((response) => {
      if (response && response.success) {
        clearAction()
        caller.setState({ success: true, inProcess: false, message: '' })
      } else {
        caller.setState({
          success: false,
          message: (response && response.error) || failureMessage,
          inProcess: false,
        })
      }
    })
    .catch((err) => {
      caller.setState({
        success: false,
        message: Helper.getErrorMsg(err),
        inProcess: false,
      })
    })
}

function CustomFilters(props) {
  const { orgId, pageKey, excludeSubOrgReferencingCustomFilters } = props
  const [pendingAction, setPendingAction] = useState(null)
  const filters = orgId && CRMHelper.getCustomFilters(orgId, pageKey, {
    excludeSubOrgReferencing: excludeSubOrgReferencingCustomFilters,
  })

  if (!filters || filters.length === 0) {
    return (
      <div className={css(Styles.customFilterDialogList)}>
        <p className={css(Styles.customFilterDialogEmptyText)}>{Helper.getString('noSavedQuickFilters')}</p>
      </div>
    )
  }

  const filterRows = CRMHelper.splitCardsIntoBalancedRows(
    filters,
    CRMHelper.getCustomFiltersMaxPerRow(orgId, filters.length)
  )

  return (
    <>
      <div className={css(Styles.customFilterDialogList)}>
        <List disablePadding>
          {filterRows.map((row, rowIndex) => (
            <div key={rowIndex} className={css(Styles.customFilterDialogRow)}>
              {row.map((filter) => (
                <ListItem
                  key={filter.label}
                  disableGutters
                  className={css(Styles.customFilterDialogItem)}
                  style={{ width: '100%' }}
                >
                  <div
                    className={css(Styles.customFilterDialogLabel)}
                    onClick={() => {
                      if (props.onCustomFilterSelect) {
                        props.onCustomFilterSelect(filter.value)
                      }
                    }}
                  >
                    <p className={css(Styles.customFilterDialogText)}>{filter.label}</p>
                  </div>
                  {CRMHelper.canManageCustomFilters() && (
                    <div
                      className={css(Styles.customFilterDialogActions)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MenuItemsDropdown
                        current={filter}
                        onMenuItems={(current) => CRMHelper.getCustomFilterMenuItems(current, setPendingAction)}
                        iconColor='gray'
                        zIndex={13002}
                      />
                    </div>
                  )}
                </ListItem>
              ))}
            </div>
          ))}
        </List>
      </div>
      <DynamicFormDialog
        key={pendingAction
          ? (pendingAction.type === 'edit' ? pendingAction.filter.label : 'delete-' + pendingAction.filter.label)
          : 'custom-filter-action-dialog'}
        isOpen={!!pendingAction}
        onClosed={() => setPendingAction(null)}
        {...customFilterNestedDialogProps}
        fullWidth={!!(pendingAction && pendingAction.type === 'edit')}
        formDefinition={CRMHelper.getCustomFilterActionFormDef(pendingAction)}
        customSubmit={(options) => handleCustomFilterActionSubmit(
          options,
          pendingAction,
          props,
          () => setPendingAction(null)
        )}
      />
    </>
  )
}

CRMHelper.CustomFilters = CustomFilters

const Styles = StyleSheet.create({
  spanValue: {
    fontSize: 14
  },
  infoCellMobile: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 2,
  },
  infoFieldMobile: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '50vw'
  },
  previewLabel: {
    fontWeight: 500
  },
  phoneValue: {
    cursor: 'pointer',
  },
  line3: {
    fontSize: 12,
    color: 'grey'
  },
  customFilterDialogList: {
    width: '100%',
    minWidth: 400,
    boxSizing: 'border-box',
    '@media (max-width: 1024px)': {
      minWidth: 0,
    },
  },
  customFilterDialogRow: {
    display: 'flex',
    gap: 10,
    margin: '6px 0',
    '@media (max-width: 1024px)': {
      width: '100%',
    },
  },
  customFilterDialogItem: {
    backgroundColor: '#f9f9f9',
    margin: 0,
    borderRadius: 6,
    display: 'flex',
    flex: 1,
    minWidth: 0,
    minHeight: 52,
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
    '@media (max-width: 1024px)': {
      width: '100%',
    },
  },
  customFilterDialogLabel: {
    flex: 1,
    minWidth: 0,
    padding: '12px 8px 12px 14px',
    cursor: 'pointer',
  },
  customFilterDialogText: {
    color: AppTheme.primaryColor,
    fontSize: 16,
    fontWeight: 500,
    textDecoration: 'none',
    textAlign: 'left',
    margin: 0,
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    lineHeight: 1.4,
  },
  customFilterDialogActions: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    alignSelf: 'flex-start',
    padding: '12px 8px 12px 0',
  },
  customFilterDialogEmptyText: {
    color: 'gray',
    fontSize: 16,
    textAlign: 'center',
    margin: 0,
    padding: '24px 0',
    lineHeight: 1.4,
  },
})